import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  BannerLibraryModal,
  GenerationInputMode,
  GenerationInputModeTabs,
  ProductGenerationPanel,
  ProductImageSelectionModal,
  ProductInfoCopyModal,
  ProductSelectionData,
  ProductSelectionModal,
  SelectedBanner,
  SelectedProductImage,
} from './index';
import MangaStyleSelectModal, { SelectedMangaStyle } from './MangaStyleSelectModal';
import SmartImageEditModal from './SmartImageEditModal';
import Loading from '../common/Loading';
import BetaAlert from '../common/BetaAlert';
import { useSetAdmanPageContext } from '../adman/useAdmanPageContext';
import { useAdmanRefreshHandler } from '../adman/useAdmanRefreshHandler';
import {
  FolderOpen,
  FileText,
  FileEdit,
  X,
  Check,
  RefreshCw,
  Download,
  BookOpen,
  Plus,
  Users,
  User,
  Edit3,
  MessageSquare,
  Play,
  Loader2,
  AlertCircle,
  Sparkles,
  Wand2,
  Image as ImageIcon,
  ZoomIn,
  ChevronLeft,
  ChevronRight,
  Star,
  Eye,
  Upload,
  CheckCircle,
  Loader,
  Trash2,
  Library,
  Settings,
  HelpCircle,
  Share2
} from 'lucide-react';
import mangaGenerationService, {
  MangaPage,
  MangaProject,
  MangaCharacter,
  MangaDialogue,
  MangaGenerationJob,
  MangaStyleType,
  MangaPageImageVersion,
  MangaPublicationData,
} from '../../services/api/mangaGenerationService';
import type { GeneratedImage } from '../../services/api/imageGenerationService';
import s3UploadService from '../../services/api/s3UploadService';
import { buildProductGenerationPrompt } from '../../utils/productGenerationPrompt';
import { IMAGE_GENERATION_MODEL } from '../../constants/imageModel';
import { TaskySelect, useConfirm } from '../tasky';
import {
  MANGA_BTN_PRIMARY_BASE,
  MANGA_BTN_PRIMARY_SM,
  MANGA_BTN_SECONDARY_BASE,
  MANGA_BTN_SECONDARY_SM,
  MANGA_DEFAULT_CTA_TEXT,
  MANGA_INPUT,
  MANGA_PAGE_COUNT_OPTIONS,
  MANGA_SETTING_LABEL,
  mangaAspectClass,
} from '../../constants/manga';
import MangaPublicationPanel from '../manga/MangaPublicationPanel';
import MangaStoryScroll from '../manga/MangaStoryScroll';

// 画像アップロード用のインターフェース
interface UploadedImage {
  id: string;
  url: string;
  fileName: string;
  uploadedAt: Date;
}

interface MangaGenerationTabProps {
  onError?: (error: string) => void;
}

type GenerationStep = 'idle' | 'creating' | 'editing' | 'generating' | 'complete';

// --- 旧 MangaGenerationTab.scss の共通スタイルを Tailwind ユーティリティ化した定数 ---
// purge 対策のため、必ず完全なクラス名文字列で保持すること（動的生成禁止）。
// 競合するユーティリティ（px-* と px-* 等）は同一文字列に混ぜず、サイズ違いは別定数にする。
const MANGA_BTN_PRIMARY = `${MANGA_BTN_PRIMARY_BASE} px-6 py-3 text-sm`;
// .manga-btn-primary.retry — 再試行ボタン（amber グラデーション。SCSS の mix() を hex 化）
// NOTE: 旧 SCSS では `.manga-btn-primary.retry`(0,2,0) が `.manga-btn-primary:disabled`(0,2,0) より
// 後に定義されており、disabled でもグラデーションが残る実効値だった。そのため disabled 背景は付けない。
const MANGA_BTN_RETRY =
  'inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-[linear-gradient(135deg,#f59e0b,#c47e09)] px-6 py-3 text-sm font-semibold text-white transition-all duration-200 hover:enabled:-translate-y-px hover:enabled:bg-[linear-gradient(135deg,#dd8e0a,#b87708)] disabled:cursor-not-allowed';
// .manga-btn-primary.manga-btn-download — padding は primary と同値、font-size のみ 15px
const MANGA_BTN_DOWNLOAD = `${MANGA_BTN_PRIMARY_BASE} px-6 py-3 text-[15px]`;

const MANGA_BTN_SECONDARY = `${MANGA_BTN_SECONDARY_BASE} px-6 py-3 text-sm`;

const MANGA_BTN_ICON =
  'inline-flex cursor-pointer items-center justify-center rounded-lg border border-gray-300 bg-white p-2 text-gray-600 transition-all duration-200 hover:border-indigo-500 hover:bg-indigo-500/5 hover:text-indigo-500';

const MANGA_BTN_GHOST =
  'flex cursor-pointer items-center gap-1.5 rounded-lg bg-transparent px-4 py-2 text-sm font-medium text-gray-600 transition-all duration-200 hover:bg-gray-100 hover:text-gray-800';

const MANGA_TEXTAREA = `${MANGA_INPUT} min-h-[120px] resize-y`;

const MANGA_ERROR =
  'mt-4 flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-500';

// @mixin card-shadow / card-shadow-hover
const MANGA_CARD_SHADOW = 'shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)]';
const MANGA_CARD_SHADOW_HOVER = 'shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)]';
// .manga-setup-form .manga-form-label（display:block で後勝ちしていた実効値）
const MANGA_FORM_LABEL = 'mb-2 block text-sm font-medium text-gray-700';
// 参考画像サムネイル（.manga-setup-form .manga-reference-image-item 系）
const MANGA_REF_IMAGE_ITEM =
  'group relative h-20 w-20 overflow-hidden rounded-lg border-2 border-purple-500';
const MANGA_REF_IMAGE_ITEM_UPLOADED =
  "group relative h-20 w-20 overflow-hidden rounded-lg border-2 border-green-500 after:absolute after:left-1 after:top-1 after:h-2 after:w-2 after:rounded-full after:bg-green-500 after:content-['']";
const MANGA_REF_IMAGE_IMG = 'block h-full w-full bg-neutral-100 object-contain';
const MANGA_REF_IMAGE_OVERLAY =
  'absolute inset-0 flex items-start justify-end bg-black/30 p-2 opacity-0 transition-all duration-200 group-hover:opacity-100';
const MANGA_REF_REMOVE_BTN =
  'flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-white/90 p-0 text-red-500 transition-all duration-200 hover:bg-red-500 hover:text-white';
const MANGA_REF_IMAGE_NAME =
  'block overflow-hidden text-ellipsis whitespace-nowrap bg-white p-2 text-center text-[11px] text-gray-600';

// 生成中フェーズのステージインジケータ（.manga-stage / .manga-stage-label）
const MANGA_STAGE_ICON: Record<string, string> = {
  pending:
    'flex h-11 w-11 items-center justify-center rounded-full bg-gray-100 text-gray-400 transition-all duration-200',
  active:
    'flex h-11 w-11 items-center justify-center rounded-full bg-indigo-500/10 text-indigo-500 shadow-[0_0_0_4px_rgba(99,102,241,0.1)] transition-all duration-200',
  completed:
    'flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500 text-white transition-all duration-200',
};
const MANGA_STAGE_LABEL: Record<string, string> = {
  pending: 'text-xs font-medium text-gray-400 transition-all duration-200',
  active: 'text-xs font-semibold text-indigo-500 transition-all duration-200',
  completed: 'text-xs font-medium text-emerald-500 transition-all duration-200',
};
// 生成中フェーズのページプレビュー（.manga-preview-item / .manga-preview-item-badge）
const MANGA_PREVIEW_ITEM: Record<string, string> = {
  pending: 'overflow-hidden rounded-lg border-2 border-gray-200 bg-white transition-all duration-200',
  completed: 'overflow-hidden rounded-lg border-2 border-emerald-500 bg-white transition-all duration-200',
  generating:
    'overflow-hidden rounded-lg border-2 border-indigo-500 bg-white shadow-[0_0_0_3px_rgba(99,102,241,0.15)] transition-all duration-200',
  failed: 'overflow-hidden rounded-lg border-2 border-red-500 bg-white transition-all duration-200',
};
const MANGA_PREVIEW_BADGE: Record<string, string> = {
  pending: 'flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-[10px] text-gray-500',
  completed: 'flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 text-[10px] text-white',
  generating: 'flex h-5 w-5 items-center justify-center rounded-full bg-indigo-500/10 text-[10px] text-indigo-500',
  failed: 'flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-[10px] text-white',
};

// 完了フェーズの統計カード（.manga-completed-stat-*）
const MANGA_STAT_CARD = 'flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4';
const MANGA_STAT_CARD_ERROR = 'flex items-center gap-3 rounded-lg border border-red-500/20 bg-red-500/5 p-4';
const MANGA_STAT_ICON =
  'flex h-10 w-10 items-center justify-center rounded-[10px] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)]';
const MANGA_STAT_VALUE = 'text-xl font-bold leading-none';
const MANGA_STAT_LABEL = 'mt-0.5 text-xs text-gray-500';
// 完了フェーズのギャラリー（.manga-gallery-item / .manga-gallery-item-badge / action btn）
const MANGA_GALLERY_ITEM: Record<string, string> = {
  pending:
    'group overflow-hidden rounded-lg border-2 border-gray-200 bg-white transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-500 hover:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)]',
  completed:
    'group overflow-hidden rounded-lg border-2 border-emerald-500/30 bg-white transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-500 hover:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)]',
  generating:
    'group overflow-hidden rounded-lg border-2 border-gray-200 bg-white transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-500 hover:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)]',
  failed:
    'group overflow-hidden rounded-lg border-2 border-red-500/30 bg-white transition-all duration-200 hover:-translate-y-0.5 hover:border-indigo-500 hover:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)]',
};
const MANGA_GALLERY_BADGE: Record<string, string> = {
  pending: 'absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-300 text-[10px] text-white',
  completed: 'absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500 text-[10px] text-white',
  generating: 'absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500 text-[10px] text-white',
  failed: 'absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-[10px] text-white',
};
const MANGA_GALLERY_ACTION_BTN =
  'flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-white text-gray-700 shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-200 hover:scale-110 hover:bg-indigo-500 hover:text-white';

// 履歴カード（.manga-history-card-badge / .manga-history-action-btn）
const MANGA_HISTORY_BADGE =
  'inline-flex items-center gap-1 rounded-xl px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur-[4px]';
// NOTE: .style は原文どおり白文字 × white/90 背景（＝文字が読めない既存バグ）。
// 本 Wave は「見た目を変えない」方針のためそのまま維持する。
const MANGA_HISTORY_BADGE_STYLE =
  'inline-flex items-center gap-1 rounded-xl bg-white/90 px-2 py-1 text-sm font-semibold text-white backdrop-blur-[4px]';
const MANGA_HISTORY_BADGE_STATUS: Record<string, string> = {
  default: `${MANGA_HISTORY_BADGE} bg-amber-500/90`,
  draft: `${MANGA_HISTORY_BADGE} bg-amber-500/90`,
  in_progress: `${MANGA_HISTORY_BADGE} bg-amber-500/90`,
  completed: `${MANGA_HISTORY_BADGE} bg-amber-500/90`,
  generating: `${MANGA_HISTORY_BADGE} bg-indigo-500/90`,
  generating_characters: `${MANGA_HISTORY_BADGE} bg-indigo-500/90`,
};
const MANGA_HISTORY_ACTION_BTN =
  'inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-[13px] font-medium text-gray-700 transition-all duration-200 hover:border-gray-400 hover:bg-gray-100';
const MANGA_HISTORY_ACTION_BTN_PRIMARY =
  'inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-indigo-500 bg-indigo-500 px-3.5 py-2.5 text-[13px] font-medium text-white transition-all duration-200 hover:border-indigo-600 hover:bg-indigo-600';
const MANGA_HISTORY_ACTION_BTN_DANGER =
  'inline-flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-red-500 bg-white px-3.5 py-2.5 text-[13px] font-medium text-red-500 transition-all duration-200 hover:bg-red-500/5';

// ページサムネイル（.manga-thumbnail-mini の selected / generating 排他組み合わせ）
// NOTE: `.manga-thumbnail-strip` クラス名だけは globals.css のスクロールバー装飾のため tsx に残す。
const MANGA_THUMB_MINI_BASE =
  'relative h-20 w-[60px] shrink-0 cursor-pointer overflow-hidden rounded border-2 bg-gray-100 transition-all duration-200';
const MANGA_THUMB_MINI_SELECTED = 'scale-110 border-indigo-500 shadow-[0_2px_8px_rgba(99,102,241,0.3)]';
const MANGA_THUMB_MINI_GENERATING =
  "pointer-events-none opacity-50 after:absolute after:inset-0 after:bg-white/50 after:content-['']";
const mangaThumbnailMiniClass = (isSelected: boolean, isGenerating: boolean): string => {
  if (isGenerating && isSelected) {
    return `${MANGA_THUMB_MINI_BASE} ${MANGA_THUMB_MINI_SELECTED} ${MANGA_THUMB_MINI_GENERATING}`;
  }
  if (isGenerating) {
    return `${MANGA_THUMB_MINI_BASE} border-transparent ${MANGA_THUMB_MINI_GENERATING}`;
  }
  if (isSelected) {
    return `${MANGA_THUMB_MINI_BASE} opacity-100 ${MANGA_THUMB_MINI_SELECTED}`;
  }
  return `${MANGA_THUMB_MINI_BASE} border-transparent opacity-60 hover:scale-105 hover:opacity-90`;
};

// キャラクターカード（.manga-character-card / -image / .character-status-badge）
// NOTE: 旧 SCSS では `.manga-character-card:hover { border-color }` より
// `.manga-character-card.protagonist { border-left }` が後勝ちして左罫線は色を保っていた。
// Tailwind では hover 変種が後に出るため `hover:border-l-*` を明示して同じ実効値にする。
const MANGA_CHAR_CARD_BASE =
  'group flex cursor-pointer items-stretch overflow-hidden rounded-lg border border-gray-200 bg-gray-50 transition-all duration-200 hover:border-indigo-500 hover:bg-white hover:shadow-[0_2px_8px_rgba(99,102,241,0.15)]';
const mangaCharacterCardClass = (role: string): string => {
  if (role === 'protagonist') return `${MANGA_CHAR_CARD_BASE} border-l-[3px] border-l-amber-500 hover:border-l-amber-500`;
  if (role === 'supporting') return `${MANGA_CHAR_CARD_BASE} border-l-[3px] border-l-indigo-500 hover:border-l-indigo-500`;
  return `${MANGA_CHAR_CARD_BASE} border-l-[3px] border-l-gray-400 hover:border-l-gray-400`;
};
const MANGA_CHAR_CARD_IMAGE_BASE =
  'relative flex min-h-[64px] w-16 shrink-0 items-center justify-center';
const mangaCharacterCardImageClass = (role: string): string => {
  if (role === 'protagonist') return `${MANGA_CHAR_CARD_IMAGE_BASE} bg-amber-500/[0.15]`;
  if (role === 'supporting') return `${MANGA_CHAR_CARD_IMAGE_BASE} bg-indigo-500/[0.15]`;
  return `${MANGA_CHAR_CARD_IMAGE_BASE} bg-gray-200`;
};
// `.manga-character-card-image .character-status-badge`（0,2,0）が位置・サイズで勝ち、
// 単体 `.character-status-badge`（0,1,0）の font-weight / border だけが残る実効値。
const MANGA_CHAR_STATUS_BADGE =
  'absolute bottom-1 right-1 flex h-[18px] w-[18px] items-center justify-center rounded-full border-[1.5px] border-white text-[0.65rem] font-bold shadow-[0_1px_3px_rgba(0,0,0,0.2)]';
const MANGA_CHAR_REGEN_BTN =
  'absolute right-1 top-1 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-white/90 text-gray-600 opacity-0 transition-all duration-200 hover:bg-white hover:text-indigo-500 disabled:cursor-not-allowed disabled:opacity-50 [.group:hover_&]:opacity-100';

// キャラクター編集フォーム（.manga-character-editor 配下）
const MANGA_CHAR_AVATAR_EDIT: Record<string, string> = {
  protagonist:
    'flex h-12 w-12 items-center justify-center rounded-full bg-[linear-gradient(135deg,#fbbf24_0%,#f59e0b_100%)] text-lg font-bold text-white',
  supporting:
    'flex h-12 w-12 items-center justify-center rounded-full bg-[linear-gradient(135deg,#6366f1_0%,#4f46e5_100%)] text-lg font-bold text-white',
  extra:
    'flex h-12 w-12 items-center justify-center rounded-full bg-[linear-gradient(135deg,#9ca3af_0%,#6b7280_100%)] text-lg font-bold text-white',
};
const MANGA_EDITOR_FIELD_LABEL = 'mb-2 flex items-center gap-1.5 text-[13px] font-semibold text-gray-700 [&>svg]:text-gray-500';
const MANGA_EDITOR_FIELD_INPUT =
  'w-full rounded-lg border border-gray-200 bg-white px-3.5 py-3 text-sm transition-all duration-200 placeholder:text-gray-400 focus:border-indigo-500 focus:shadow-[0_0_0_3px_rgba(99,102,241,0.1)] focus:outline-none';
const MANGA_EDITOR_FIELD_TEXTAREA = `${MANGA_EDITOR_FIELD_INPUT} min-h-[80px] resize-y`;
const MANGA_ROLE_OPTION_BASE =
  'flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-[13px] font-medium transition-all duration-200';
const mangaRoleOptionClass = (role: string, isActive: boolean): string => {
  if (!isActive) {
    return `${MANGA_ROLE_OPTION_BASE} border border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50`;
  }
  if (role === 'protagonist') return `${MANGA_ROLE_OPTION_BASE} border border-amber-500 bg-amber-500/10 text-amber-700`;
  if (role === 'supporting') return `${MANGA_ROLE_OPTION_BASE} border border-indigo-500 bg-indigo-500/10 text-indigo-600`;
  return `${MANGA_ROLE_OPTION_BASE} border border-gray-500 bg-gray-500/10 text-gray-700`;
};

// ページ編集パネル（.manga-edit-panel 配下）
const MANGA_EDIT_LABEL = 'flex items-center gap-1.5 text-sm font-medium text-gray-600 [&>svg]:text-gray-400';
const MANGA_LABEL_HINT_SM = 'ml-1 text-xs font-normal text-gray-400';
const MANGA_BTN_ADD_SM =
  'flex cursor-pointer items-center gap-1 rounded border border-gray-300 bg-transparent px-2 py-1 text-xs text-gray-600 transition-all duration-200 hover:border-gray-400 hover:bg-gray-100';
// .manga-correction-header .manga-btn-add-small だけ背景が白（0,2,0 で上書きされていた）
const MANGA_BTN_ADD_SM_WHITE =
  'flex cursor-pointer items-center gap-1 rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-600 transition-all duration-200 hover:border-gray-400 hover:bg-gray-100';
const MANGA_INPUT_SM_PANEL =
  'flex-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm focus:border-indigo-500 focus:shadow-[0_0_0_2px_rgba(99,102,241,0.2)] focus:outline-none';
const MANGA_THUMB_REMOVE_SM =
  'absolute right-0.5 top-0.5 flex h-4 w-4 cursor-pointer items-center justify-center rounded-full bg-gray-800/70 p-0 text-white opacity-0 transition-opacity duration-200 hover:bg-red-500/90 group-hover:opacity-100';
const MANGA_THUMB_REMOVE_UPLOADED =
  'absolute right-0.5 top-0.5 flex h-[18px] w-[18px] cursor-pointer items-center justify-center rounded-full bg-black/70 p-0 text-white opacity-0 transition-opacity duration-200 hover:bg-red-500/90 group-hover:opacity-100';
// ::after のフェードアウト用グラデーションは Tailwind の after: 変種で維持
const MANGA_PRODUCT_INFO_PREVIEW =
  "relative mt-2 max-h-[100px] overflow-hidden whitespace-pre-wrap break-words rounded-lg bg-gray-50 p-3 text-[13px] leading-[1.5] text-gray-600 after:pointer-events-none after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[30px] after:bg-[linear-gradient(transparent,#f9fafb)] after:content-['']";

// 画像ライトボックス（.manga-image-modal 配下）
const MANGA_MODAL_NAV =
  'absolute top-1/2 z-10 -translate-y-1/2 cursor-pointer rounded-lg bg-white/10 px-3 py-4 text-white transition-all duration-200 hover:bg-white/20';
const MANGA_MODAL_ACTION_BTN =
  'inline-flex min-h-[36px] cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-white/20 px-3.5 py-2 text-sm font-bold text-white transition-all duration-200 hover:-translate-y-px hover:border-white/[0.38]';

const MangaGenerationTab: React.FC<MangaGenerationTabProps> = ({ onError }) => {
  const confirm = useConfirm();

  // Form state
  const [inputMode, setInputMode] = useState<GenerationInputMode>('product');
  const [productInfo, setProductInfo] = useState('');
  const [selectedProductData, setSelectedProductData] = useState<ProductSelectionData | null>(null);
  // 広告自動化マンに「今この商品を見ている」と伝える（F20-a）。既存ロジックには触れない。
  useSetAdmanPageContext(
    selectedProductData ? { product_ids: [selectedProductData.product.id] } : null
  );
  const [additionalInstructions, setAdditionalInstructions] = useState('');
  const [pageCount, setPageCount] = useState(4);
  const [aspectRatio, setAspectRatio] = useState<'9:16' | '1:1' | '16:9'>('1:1');
  const [colorMode, setColorMode] = useState<'color' | 'monochrome'>('color');
  const [mangaVisualStyle, setMangaVisualStyle] = useState('modern');

  // Style selection (API-driven)
  const [mangaStyles, setMangaStyles] = useState<MangaStyleType[]>([]);
  const [selectedMangaStyleId, setSelectedMangaStyleId] = useState<number | null>(null);
  const [selectedMangaStyleData, setSelectedMangaStyleData] = useState<SelectedMangaStyle | null>(null);
  const [showMangaStyleModal, setShowMangaStyleModal] = useState(false);
  const [isLoadingStyles, setIsLoadingStyles] = useState(false);

  // Reference images (product images)
  const [showImageSelectionModal, setShowImageSelectionModal] = useState(false);
  const [showProductSelectionModal, setShowProductSelectionModal] = useState(false);
  const [showFreeTextProductInfoModal, setShowFreeTextProductInfoModal] = useState(false);
  const [referenceImages, setReferenceImages] = useState<SelectedProductImage[]>([]);

  // Reference images (banner library + upload)
  const [showBannerLibraryModal, setShowBannerLibraryModal] = useState(false);
  const [uploadedReferenceImages, setUploadedReferenceImages] = useState<Array<{
    id: string;
    url: string;
    s3Key?: string;
    imageData?: string;
    fileName: string;
    source: 'banner' | 'upload';
  }>>([]);
  const uploadInputRef = useRef<HTMLInputElement>(null);

  // Generation state
  const [isLoading, setIsLoading] = useState(false);
  const [generationStep, setGenerationStep] = useState<GenerationStep>('idle');
  const [error, setError] = useState<string | null>(null);

  // Manga project state
  const [mangaProject, setMangaProject] = useState<MangaProject | null>(null);
  const [generationJob, setGenerationJob] = useState<MangaGenerationJob | null>(null);

  // 公開設定（公開URL / 最終コマの CTA）とストーリープレビュー
  const [showStoryPreview, setShowStoryPreview] = useState(false);
  const [editingPageNumber, setEditingPageNumber] = useState<number | null>(null);
  const [editingCharacterId, setEditingCharacterId] = useState<string | null>(null);

  // Character image generation state
  const [isGeneratingCharacterImages, setIsGeneratingCharacterImages] = useState(false);
  const [generatingCharacterId, setGeneratingCharacterId] = useState<string | null>(null);

  // Page-level editing state (new 2-column layout)
  const [selectedPageNumber, setSelectedPageNumber] = useState<number>(1);
  const [generatingPages, setGeneratingPages] = useState<Set<number>>(new Set());

  // 編集画面用の商材素材選択
  const [showEditImageSelectionModal, setShowEditImageSelectionModal] = useState(false);
  const [pageReferenceImages, setPageReferenceImages] = useState<{
    [pageNumber: number]: SelectedProductImage[];
  }>({});

  // 画像アップロード用
  const [pageUploadedImages, setPageUploadedImages] = useState<{
    [pageNumber: number]: UploadedImage[];
  }>({});
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // 編集画面用の商品情報モーダル
  const [showEditProductInfoModal, setShowEditProductInfoModal] = useState(false);

  // Polling
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Scroll refs
  const editSectionRef = useRef<HTMLDivElement>(null);
  const completeSectionRef = useRef<HTMLDivElement>(null);

  // Download state
  const [isDownloading, setIsDownloading] = useState(false);
  const [showSmartPageSelector, setShowSmartPageSelector] = useState(false);
  const [smartEditingPage, setSmartEditingPage] = useState<MangaPage | null>(null);

  // History state
  const [projectHistory, setProjectHistory] = useState<MangaProject[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyTotalCount, setHistoryTotalCount] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(true);

  // Image modal state
  const [modalImageUrl, setModalImageUrl] = useState<string | null>(null);
  const [modalPageIndex, setModalPageIndex] = useState<number>(0);
  const [modalPages, setModalPages] = useState<MangaPage[]>([]);

  // Fetch manga styles from API
  const fetchMangaStyles = useCallback(async () => {
    try {
      setIsLoadingStyles(true);
      const response = await mangaGenerationService.getMangaStyles();
      if (response.success && response.data) {
        setMangaStyles(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch manga styles:', err);
    } finally {
      setIsLoadingStyles(false);
    }
  }, []);

  useEffect(() => {
    fetchMangaStyles();
  }, [fetchMangaStyles]);

  const visualStyleOptions = [
    { id: 'modern', name: 'モダン', description: 'クリーンでシンプルなスタイル', emoji: '✨' },
    { id: 'classic', name: 'クラシック', description: '伝統的な漫画スタイル', emoji: '📚' },
    { id: 'pop', name: 'ポップ', description: 'カラフルで明るいスタイル', emoji: '🎨' },
    { id: 'business', name: 'ビジネス', description: 'プロフェッショナルなスタイル', emoji: '💼' },
  ];

  const aspectRatioOptions = [
    { id: '1:1', name: 'スクエア（横1080×縦1080px）', description: 'Instagram向け', emoji: '⬜' },
    { id: '9:16', name: '縦長（横1080×縦1920px）', description: 'SNS向け', emoji: '📱' },
    { id: '16:9', name: '横長（横1920×縦1080px）', description: 'バナー向け', emoji: '🖥️' },
  ];

  const colorModeOptions = [
    { id: 'color', name: 'カラー', description: 'フルカラー漫画', emoji: '🎨' },
    { id: 'monochrome', name: '白黒', description: 'モノクロ漫画', emoji: '⬛' },
  ];

  // Fetch project history
  const fetchProjectHistory = useCallback(async (page: number = 1) => {
    try {
      setIsLoadingHistory(true);
      const response = await mangaGenerationService.getProjectHistory(page);
      if (response.success) {
        const newProjects = response.data.data;
        if (page === 1) {
          setProjectHistory(newProjects);
        } else {
          setProjectHistory(prev => [...prev, ...newProjects]);
        }
        setHistoryTotalPages(response.data.last_page);
        setHistoryTotalCount(response.data.total);
        setHasMoreHistory(page < response.data.last_page);
      }
    } catch (err: any) {
      console.error('Failed to fetch project history:', err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);

  // Refresh history after completion
  const refreshHistory = useCallback(() => {
    setHistoryPage(1);
    setProjectHistory([]);
    fetchProjectHistory(1);
  }, [fetchProjectHistory]);

  // 広告自動化マンの「更新する」ボタンから呼べるようにする（F23-c）。
  useAdmanRefreshHandler(refreshHistory);

  // Deep-link: auto-select project when ?project=<uuid> query param is present
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkProcessedRef = useRef(false);
  useEffect(() => {
    if (deepLinkProcessedRef.current) return;
    const projectId = searchParams.get('project');
    if (!projectId) return;
    deepLinkProcessedRef.current = true;
    (async () => {
      try {
        const response = await mangaGenerationService.getProject(projectId);
        if (response.success && response.data) {
          setMangaProject(response.data);
          setGenerationStep(response.data.status === 'completed' ? 'complete' : 'editing');
          // Clean URL so reload doesn't keep re-selecting
          const next = new URLSearchParams(searchParams);
          next.delete('project');
          setSearchParams(next, { replace: true });
        }
      } catch (err) {
        console.error('Deep-link manga project load failed:', err);
      }
    })();
  }, [searchParams, setSearchParams]);

  // Poll for generation status
  const startPolling = useCallback((projectId: string) => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    pollingRef.current = setInterval(async () => {
      try {
        const response = await mangaGenerationService.getGenerationStatus(projectId);
        if (response.success) {
          setMangaProject(response.data.project);
          setGenerationJob(response.data.job);

          // Update generatingPages based on page status
          const completedOrFailedPages = (response.data.project.pages || [])
            .filter((p: MangaPage) => p.generation_status === 'completed' || p.generation_status === 'failed')
            .map((p: MangaPage) => p.page_number);

          setGeneratingPages(prev => {
            const next = new Set(prev);
            completedOrFailedPages.forEach((pn: number) => next.delete(pn));
            return next;
          });

          // Check if generation is complete
          if (response.data.job?.status === 'completed' || response.data.project.status === 'completed') {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            setGenerationStep('complete');
            setGeneratingPages(new Set()); // Clear all generating pages
            // Refresh history after completion
            refreshHistory();
          } else if (response.data.job?.status === 'failed') {
            if (pollingRef.current) {
              clearInterval(pollingRef.current);
              pollingRef.current = null;
            }
            setError(response.data.job.error_message || '生成に失敗しました');
            setGenerationStep('editing');
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000);
  }, [refreshHistory]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Fetch project history on mount and when page changes
  useEffect(() => {
    fetchProjectHistory(historyPage);
  }, [historyPage, fetchProjectHistory]);

  // Scroll to relevant section when switching modes
  useEffect(() => {
    if (generationStep === 'editing' && editSectionRef.current) {
      setTimeout(() => {
        editSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } else if (generationStep === 'complete' && completeSectionRef.current) {
      setTimeout(() => {
        completeSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  }, [generationStep]);

  const handleReferenceImagesSelect = (selected: SelectedProductImage[]) => {
    setReferenceImages(selected);
  };

  const handleRemoveReferenceImage = (imageId: number) => {
    setReferenceImages(prev => prev.filter(img => img.id !== imageId));
  };

  const handleCopyToPrompt = (text: string) => {
    setProductInfo(prev => prev ? `${prev}\n\n${text}` : text);
  };

  const handleProductSelected = (data: ProductSelectionData) => {
    setSelectedProductData(data);
    if (data.selectedImages.length > 0) {
      setReferenceImages(data.selectedImages.map((image) => ({
        id: image.id,
        url: image.url,
        s3Key: image.file_path,
        productId: image.product_id,
        productName: data.product.name,
        fileName: image.file_name,
        category: image.category,
      })));
    }
    setShowProductSelectionModal(false);
  };

  // Handle banner library selection
  const handleBannerLibrarySelect = (selectedBanners: SelectedBanner[]) => {
    const newImages = selectedBanners.map(banner => ({
      id: `banner-${banner.id}-${Date.now()}`,
      url: banner.url,
      s3Key: banner.s3Key,
      fileName: banner.fileName,
      source: 'banner' as const,
    }));
    setUploadedReferenceImages(prev => [...prev, ...newImages]);
    setShowBannerLibraryModal(false);
  };

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;

      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        setUploadedReferenceImages(prev => [...prev, {
          id: `upload-${Date.now()}-${i}`,
          url: base64,
          imageData: base64,
          fileName: file.name,
          source: 'upload',
        }]);
      };
      reader.readAsDataURL(file);
    }

    // Reset input
    if (uploadInputRef.current) {
      uploadInputRef.current.value = '';
    }
  };

  // Remove uploaded reference image (setup form)
  const handleRemoveUploadedRefImage = (imageId: string) => {
    setUploadedReferenceImages(prev => prev.filter(img => img.id !== imageId));
  };

  // Create project and generate story structure
  const handleCreateProject = async () => {
    if (inputMode === 'product' && !selectedProductData) {
      setError('商品を選択してください');
      return;
    }
    if (inputMode === 'freetext' && !productInfo.trim()) {
      setError('プロンプトを入力してください');
      return;
    }
    if (!selectedMangaStyleId && inputMode === 'product' && !additionalInstructions.trim()) {
      setError('スタイルを選択するか、ストーリーの説明を入力してください');
      return;
    }

    setIsLoading(true);
    setError(null);
    setGenerationStep('creating');

    try {
      // Build reference images array from all sources
      const allRefImages: Array<{ url?: string; s3_key?: string; image_data?: string; category: string }> = [];

      // Product images
      referenceImages.forEach(img => {
        allRefImages.push({ url: img.url, category: img.category });
      });

      // Banner library + uploaded images
      uploadedReferenceImages.forEach(img => {
        if (img.s3Key) {
          allRefImages.push({ s3_key: img.s3Key, category: 'material' });
        } else if (img.imageData) {
          allRefImages.push({ image_data: img.imageData, category: 'material' });
        }
      });

      const effectiveProductInfo = inputMode === 'product' && selectedProductData
        ? buildProductGenerationPrompt(selectedProductData, additionalInstructions)
        : productInfo.trim();
      setProductInfo(effectiveProductInfo);
      const productDataForApi = selectedProductData ? {
        id: selectedProductData.product.id,
        name: selectedProductData.product.name,
        extended_metadata: selectedProductData.product.extended_metadata as Record<string, unknown> | undefined,
      } : undefined;

      const response = await mangaGenerationService.createMangaProject({
        product_info: effectiveProductInfo,
        product_data: inputMode === 'product' ? productDataForApi : undefined,
        manga_style_id: selectedMangaStyleId || undefined,
        story_prompt: selectedMangaStyleId
          ? undefined
          : inputMode === 'product'
            ? additionalInstructions
            : productInfo.trim(),
        custom_story_prompt: selectedMangaStyleId && inputMode === 'product' && additionalInstructions.trim()
          ? additionalInstructions.trim()
          : undefined,
        page_count: pageCount,
        style: mangaVisualStyle || selectedMangaStyleData?.visualStyle || 'modern',
        aspect_ratio: aspectRatio,
        color_mode: colorMode,
        image_model: IMAGE_GENERATION_MODEL,
        reference_images: allRefImages.length > 0 ? allRefImages : undefined,
      });

      if (response.success && response.data) {
        const project = response.data;
        setMangaProject(project);
        // 初期参考画像をページ1に設定
        if (referenceImages.length > 0) {
          setPageReferenceImages({
            1: [...referenceImages]
          });
        }

        // Start polling for character images (generated in background on backend)
        if (project.characters && project.characters.length > 0) {
          setIsGeneratingCharacterImages(true);
          startCharacterImagePolling(project.id);
        }

        // 自動的に全ページ生成を開始（編集ステップをスキップ）
        try {
          const genResponse = await mangaGenerationService.startGeneration(project.id);
          if (genResponse.success) {
            setMangaProject(genResponse.data.project);
            setGenerationJob(genResponse.data.job);
            setGenerationStep('generating');
            startPolling(project.id);
          } else {
            // 生成開始に失敗した場合は編集画面へ
            setGenerationStep('editing');
            setError(genResponse.error || '生成の開始に失敗しました。編集画面から再度お試しください。');
          }
        } catch (genErr: any) {
          // 生成開始に失敗した場合は編集画面へ
          console.error('Error starting generation:', genErr);
          setGenerationStep('editing');
          setError(genErr.message || '生成の開始に失敗しました。編集画面から再度お試しください。');
        }
      } else {
        throw new Error(response.error || 'プロジェクトの作成に失敗しました');
      }
    } catch (err: any) {
      console.error('Error creating project:', err);
      setError(err.message || '漫画プロジェクトの作成中にエラーが発生しました');
      setGenerationStep('idle');
    } finally {
      setIsLoading(false);
    }
  };

  // Start batch generation
  const handleStartGeneration = async () => {
    if (!mangaProject) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await mangaGenerationService.startGeneration(mangaProject.id);

      if (response.success) {
        setMangaProject(response.data.project);
        setGenerationJob(response.data.job);
        setGenerationStep('generating');
        startPolling(mangaProject.id);
      } else {
        throw new Error(response.error || '生成の開始に失敗しました');
      }
    } catch (err: any) {
      console.error('Error starting generation:', err);
      setError(err.message || '生成の開始中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  // Update character
  const handleUpdateCharacter = async (character: MangaCharacter) => {
    if (!mangaProject) return;

    try {
      const updatedCharacters = mangaProject.characters?.map(c =>
        c.id === character.id ? character : c
      ) || [];

      const response = await mangaGenerationService.updateCharacters(
        mangaProject.id,
        updatedCharacters
      );

      if (response.success) {
        setMangaProject(response.data);
        setEditingCharacterId(null);
      }
    } catch (err: any) {
      console.error('Error updating character:', err);
      setError(err.message || 'キャラクターの更新に失敗しました');
    }
  };

  // Generate all character images
  const handleGenerateAllCharacterImages = async () => {
    if (!mangaProject) return;

    setIsGeneratingCharacterImages(true);
    setError(null);

    try {
      const response = await mangaGenerationService.generateCharacterImages(mangaProject.id);

      if (response.success) {
        setMangaProject(response.data.project);
        // Start polling for character image generation status
        startCharacterImagePolling(mangaProject.id);
      } else {
        throw new Error(response.error || 'キャラクター画像の生成に失敗しました');
      }
    } catch (err: any) {
      console.error('Error generating character images:', err);
      setError(err.message || 'キャラクター画像の生成中にエラーが発生しました');
      setIsGeneratingCharacterImages(false);
    }
  };

  // Regenerate a specific character's image
  const handleRegenerateCharacterImage = async (characterId: string) => {
    if (!mangaProject) return;

    setGeneratingCharacterId(characterId);
    setError(null);

    try {
      const response = await mangaGenerationService.regenerateCharacterImage(
        mangaProject.id,
        characterId
      );

      if (response.success) {
        setMangaProject(response.data.project);
        // Start polling for this character's image generation
        startCharacterImagePolling(mangaProject.id);
      } else {
        throw new Error(response.error || 'キャラクター画像の再生成に失敗しました');
      }
    } catch (err: any) {
      console.error('Error regenerating character image:', err);
      setError(err.message || 'キャラクター画像の再生成中にエラーが発生しました');
      setGeneratingCharacterId(null);
    }
  };

  // Poll for character image generation status
  const startCharacterImagePolling = (projectId: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await mangaGenerationService.getCharacterImagesStatus(projectId);

        if (response.success) {
          // Update characters in the project using functional update to avoid stale closure
          setMangaProject(prevProject => {
            if (!prevProject) return prevProject;

            const updatedCharacters = prevProject.characters?.map(char => {
              const status = response.data.characters.find(c => c.id === char.id);
              if (status) {
                return {
                  ...char,
                  character_image_url: status.character_image_url || char.character_image_url,
                  character_image_status: status.character_image_status,
                  character_image_error: status.character_image_error || undefined,
                };
              }
              return char;
            });

            return {
              ...prevProject,
              characters: updatedCharacters,
            };
          });

          // Check if all completed or no more generating
          const hasGenerating = response.data.characters.some(
            c => c.character_image_status === 'generating' || c.character_image_status === 'pending'
          );

          if (!hasGenerating || response.data.all_completed) {
            clearInterval(pollInterval);
            setIsGeneratingCharacterImages(false);
            setGeneratingCharacterId(null);
          }
        }
      } catch (err) {
        console.error('Error polling character image status:', err);
        clearInterval(pollInterval);
        setIsGeneratingCharacterImages(false);
        setGeneratingCharacterId(null);
      }
    }, 3000); // Poll every 3 seconds

    // Clear interval after 5 minutes (timeout)
    setTimeout(() => {
      clearInterval(pollInterval);
      setIsGeneratingCharacterImages(false);
      setGeneratingCharacterId(null);
    }, 5 * 60 * 1000);
  };

  // Update page
  const handleUpdatePage = async (pageNumber: number, data: Partial<MangaPage>) => {
    if (!mangaProject) return;

    try {
      const response = await mangaGenerationService.updatePage(mangaProject.id, pageNumber, {
        scene_description: data.scene_description,
        narration: data.narration,
        dialogues: data.dialogues,
        additional_instructions: data.additional_instructions,
      });

      if (response.success) {
        setMangaProject(response.data);
        setEditingPageNumber(null);
      }
    } catch (err: any) {
      console.error('Error updating page:', err);
      setError(err.message || 'ページの更新に失敗しました');
    }
  };

  // Regenerate a specific page
  const handleRegeneratePage = async (pageNumber: number, additionalInstructions?: string) => {
    if (!mangaProject) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await mangaGenerationService.regeneratePage(
        mangaProject.id,
        pageNumber,
        additionalInstructions
      );

      if (response.success) {
        setMangaProject(response.data.project);
        setGenerationJob(response.data.job);
        setGenerationStep('generating');
        startPolling(mangaProject.id);
      } else {
        throw new Error(response.error || '再生成の開始に失敗しました');
      }
    } catch (err: any) {
      console.error('Error regenerating page:', err);
      setError(err.message || '再生成の開始中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  // Download manga as ZIP
  const handleDownloadZip = async () => {
    if (!mangaProject) return;

    setIsDownloading(true);

    try {
      await mangaGenerationService.downloadMangaZip(mangaProject.id);
    } catch (err: any) {
      console.error('Error downloading ZIP:', err);
      setError(err.message || 'ZIPダウンロード中にエラーが発生しました');
    } finally {
      setIsDownloading(false);
    }
  };

  // --- 公開設定（Tasky 上の公開URL / 最終コマの CTA） ---
  // 入力欄と保存は MangaPublicationPanel が持つ。ここは結果のマージだけを担う。

  // Escape でストーリープレビューを閉じる
  useEffect(() => {
    if (!showStoryPreview) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowStoryPreview(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showStoryPreview]);

  // publication API の結果を表示中プロジェクトと履歴の両方へ反映する
  const applyPublicationResult = useCallback((projectId: string, data: MangaPublicationData) => {
    setMangaProject(prev =>
      prev && prev.id === projectId
        ? {
            ...prev,
            is_public: data.is_public,
            public_url: data.public_url,
            cta_text: data.cta_text,
            cta_url: data.cta_url,
          }
        : prev
    );
    setProjectHistory(prev =>
      prev.map(project =>
        project.id === projectId
          ? {
              ...project,
              is_public: data.is_public,
              public_url: data.public_url,
              cta_text: data.cta_text,
              cta_url: data.cta_url,
            }
          : project
      )
    );
  }, []);

  // ストーリープレビュー用のページ（生成完了したものだけ）
  const buildStoryPreviewPages = (project: MangaProject | null) =>
    (project?.pages ?? [])
      .filter(page => (page.generation_status ? page.generation_status === 'completed' : true))
      .map(page => ({
        page_number: page.page_number,
        image_url: page.selected_image_url ?? page.generated_image_url ?? '',
      }))
      .filter(page => Boolean(page.image_url));

  const getSmartEditablePages = useCallback((project: MangaProject | null = mangaProject) => {
    return (project?.pages || []).filter(page => Boolean(page.generated_image_url));
  }, [mangaProject]);

  const openSmartEditSelector = useCallback(() => {
    if (getSmartEditablePages().length === 0) {
      setError('編集できる生成済みページがありません');
      return;
    }
    setShowSmartPageSelector(true);
  }, [getSmartEditablePages]);

  const handleSmartPageSelect = (page: MangaPage) => {
    setShowSmartPageSelector(false);
    setSmartEditingPage(page);
  };

  const handleSmartEditApply = async (editedImage: GeneratedImage) => {
    if (!mangaProject || !smartEditingPage) return;

    const response = await mangaGenerationService.updatePageImage(
      mangaProject.id,
      smartEditingPage.page_number,
      {
        generated_image_url: editedImage.url,
        generated_s3_key: editedImage.s3_key || smartEditingPage.generated_s3_key,
        generated_image_id: editedImage.id > 0 ? editedImage.id : undefined,
      }
    );

    if (response.success) {
      setMangaProject(response.data);
      setSmartEditingPage(null);
      refreshHistory();
    } else {
      throw new Error('漫画ページへの編集結果保存に失敗しました');
    }
  };

  const smartEditImage: GeneratedImage | null = smartEditingPage?.generated_image_url ? {
    id: -smartEditingPage.page_number,
    prompt: smartEditingPage.scene_description || smartEditingPage.description || `漫画ページ ${smartEditingPage.page_number}`,
    url: smartEditingPage.generated_image_url,
    s3_key: smartEditingPage.generated_s3_key,
    file_size: 0,
    created_at: new Date().toISOString(),
    generation_source: 'image_generation',
  } : null;

  // Reset
  const handleReset = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setMangaProject(null);
    setGenerationJob(null);
    setGenerationStep('idle');
    setEditingPageNumber(null);
    setEditingCharacterId(null);
    setSelectedPageNumber(1);
    setGeneratingPages(new Set());
    setError(null);
    // 参考画像をリセット
    setReferenceImages([]);
    setPageReferenceImages({});
  };

  // Handle page selection in 2-column layout
  const handleSelectPage = (pageNumber: number) => {
    setSelectedPageNumber(pageNumber);
    setEditingPageNumber(null); // Clear old editing mode
  };

  // Handle image upload for a specific page
  const handleImageUpload = async (pageNumber: number, files: File[]) => {
    if (files.length === 0) return;

    setIsUploadingImage(true);
    setError(null);

    try {
      const uploadedImages: UploadedImage[] = [];

      for (const file of files) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          console.warn(`Skipping non-image file: ${file.name}`);
          continue;
        }

        // Upload to S3
        const response = await s3UploadService.uploadImageToS3(file);

        if (response) {
          uploadedImages.push({
            id: `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            url: response.s3_url || response.url,
            fileName: file.name,
            uploadedAt: new Date(),
          });
        }
      }

      if (uploadedImages.length > 0) {
        setPageUploadedImages(prev => ({
          ...prev,
          [pageNumber]: [...(prev[pageNumber] || []), ...uploadedImages]
        }));
      }
    } catch (err: any) {
      console.error('Image upload failed:', err);
      setError(err.message || '画像のアップロードに失敗しました');
      if (onError) {
        onError(err.message || '画像のアップロードに失敗しました');
      }
    } finally {
      setIsUploadingImage(false);
    }
  };

  // Handle uploaded image removal
  const handleRemoveUploadedImage = (pageNumber: number, imageId: string) => {
    setPageUploadedImages(prev => ({
      ...prev,
      [pageNumber]: (prev[pageNumber] || []).filter(img => img.id !== imageId)
    }));
  };

  // Handle page generation in 2-column layout (update + regenerate)
  const handleGeneratePageInLayout = async (pageNumber: number, data: Partial<MangaPage>) => {
    if (!mangaProject) return;

    setError(null);

    try {
      // Collect reference images for this page
      const referenceImages: Array<{ url: string; category?: string }> = [];

      // Add product images (商材素材)
      const productImages = pageReferenceImages[pageNumber] || [];
      productImages.forEach(img => {
        referenceImages.push({
          url: img.url,
          category: 'product'
        });
      });

      // Add uploaded images (アップロード画像)
      const uploadedImages = pageUploadedImages[pageNumber] || [];
      uploadedImages.forEach(img => {
        referenceImages.push({
          url: img.url,
          category: 'uploaded'
        });
      });

      // 1. Update page content with reference images
      const updateResponse = await mangaGenerationService.updatePage(mangaProject.id, pageNumber, {
        scene_description: data.scene_description,
        narration: data.narration,
        dialogues: data.dialogues,
        additional_instructions: data.additional_instructions,
        reference_images: referenceImages.length > 0 ? referenceImages : undefined,
      });

      if (updateResponse.success) {
        setMangaProject(updateResponse.data);
      }

      // 2. Add to generating pages
      setGeneratingPages(prev => {
        const next = new Set(prev);
        next.add(pageNumber);
        return next;
      });

      // 3. Start regeneration
      const regenResponse = await mangaGenerationService.regeneratePage(
        mangaProject.id,
        pageNumber,
        data.additional_instructions
      );

      if (regenResponse.success) {
        setMangaProject(regenResponse.data.project);
        setGenerationJob(regenResponse.data.job);
        // Don't change generationStep to 'generating' - stay in 'editing' mode
        startPolling(mangaProject.id);
      } else {
        throw new Error(regenResponse.error || '再生成の開始に失敗しました');
      }
    } catch (err: any) {
      console.error('Error generating page:', err);
      setGeneratingPages(prev => {
        const next = new Set(prev);
        next.delete(pageNumber);
        return next;
      });
      setError(err.message || 'ページの生成中にエラーが発生しました');
    }
  };

  // Image modal functions
  const openImageModal = (pages: MangaPage[], pageIndex: number) => {
    const pagesWithImages = pages.filter(p => p.generated_image_url);
    if (pagesWithImages.length === 0) return;

    const actualIndex = pagesWithImages.findIndex(p => p.page_number === pages[pageIndex].page_number);
    setModalPages(pagesWithImages);
    setModalPageIndex(actualIndex >= 0 ? actualIndex : 0);
    setModalImageUrl(pagesWithImages[actualIndex >= 0 ? actualIndex : 0].generated_image_url || null);
  };

  const closeImageModal = () => {
    setModalImageUrl(null);
    setModalPages([]);
    setModalPageIndex(0);
  };

  const navigateModal = (direction: 'prev' | 'next') => {
    const newIndex = direction === 'prev'
      ? (modalPageIndex - 1 + modalPages.length) % modalPages.length
      : (modalPageIndex + 1) % modalPages.length;
    setModalPageIndex(newIndex);
    setModalImageUrl(modalPages[newIndex].generated_image_url || null);
  };

  const handleSmartEditModalPage = () => {
    const page = modalPages[modalPageIndex];
    if (!page?.generated_image_url) return;
    closeImageModal();
    setSmartEditingPage(page);
  };

  const handleEditModalPage = () => {
    const page = modalPages[modalPageIndex];
    if (!page) return;
    closeImageModal();
    setSelectedPageNumber(page.page_number);
    setEditingPageNumber(page.page_number);
    setGenerationStep('editing');
    setTimeout(() => {
      editSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleRestoreModalVersion = async (version: MangaPageImageVersion) => {
    const page = modalPages[modalPageIndex];
    if (!mangaProject || !page?.page_number || !version.generated_image_url) return;

    try {
      const response = await mangaGenerationService.updatePageImage(
        mangaProject.id,
        page.page_number,
        {
          generated_image_url: version.generated_image_url,
          generated_s3_key: version.generated_s3_key || undefined,
          generated_image_id: version.generated_image_id || undefined,
        }
      );

      if (!response.success) {
        throw new Error('旧版への復元に失敗しました');
      }

      setMangaProject(response.data);
      const refreshedPages = response.data.pages.filter((p) => Boolean(p.generated_image_url));
      const restoredIndex = refreshedPages.findIndex((p) => p.page_number === page.page_number);
      const restoredPage = refreshedPages[restoredIndex >= 0 ? restoredIndex : 0];
      setModalPages(refreshedPages);
      setModalPageIndex(restoredIndex >= 0 ? restoredIndex : 0);
      setModalImageUrl(restoredPage?.generated_image_url || null);
      refreshHistory();
    } catch (err) {
      console.error('Failed to restore manga page version:', err);
      setError((err as Error).message || '旧版への復元に失敗しました');
    }
  };

  // Character role labels
  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'protagonist': return '主人公';
      case 'supporting': return '脇役';
      case 'extra': return 'その他';
      default: return role;
    }
  };

  // Get character name by ID
  const getCharacterName = (characterId: string) => {
    return mangaProject?.characters?.find(c => c.id === characterId)?.name || '不明';
  };

  // Handle manga style modal selection
  const handleMangaStyleSelect = (style: SelectedMangaStyle | null) => {
    if (style) {
      setSelectedMangaStyleId(style.id);
      setSelectedMangaStyleData(style);
    }
    setShowMangaStyleModal(false);
  };

  // Clear selected manga style
  const handleClearMangaStyle = () => {
    setSelectedMangaStyleId(null);
    setSelectedMangaStyleData(null);
  };

  // Render setup form - Banner page aligned layout
  const renderSetupForm = () => (
    <div className={`rounded-xl bg-white p-8 max-[768px]:p-5 ${MANGA_CARD_SHADOW}`}>
      {/* Header Section */}
      <div className="mb-8 text-center">
        <div className="mb-4 inline-flex h-16 w-16 items-center justify-center rounded-full bg-indigo-500/10 text-indigo-500">
          <BookOpen size={32} />
        </div>
        <h2 className="mb-2 flex items-center gap-3 text-2xl font-semibold text-gray-800">広告漫画を作成</h2>
        <p className="text-sm text-gray-500">商品情報とストーリーを入力して、AIが漫画のストーリー構成を生成します</p>
      </div>

      {/* Flat form layout (aligned with banner page) */}
      <div className="flex flex-col gap-5">
        <GenerationInputModeTabs value={inputMode} onChange={setInputMode} disabled={isLoading} />

        {/* Section 1: Prompt source */}
        <div>
          {inputMode === 'product' ? (
            <div data-tutorial="ads-manga-product-select">
              <ProductGenerationPanel
                selectedProductData={selectedProductData}
                additionalInstructions={additionalInstructions}
                onAdditionalInstructionsChange={setAdditionalInstructions}
                onSelectProduct={() => setShowProductSelectionModal(true)}
                disabled={isLoading}
              />
            </div>
          ) : (
            <>
              <div className="mb-2 flex items-center justify-between">
                <label className={MANGA_FORM_LABEL}>プロンプト</label>
                <button
                  type="button"
                  onClick={() => setShowFreeTextProductInfoModal(true)}
                  disabled={isLoading}
                  className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-gradient-to-r from-purple-500 to-indigo-500 px-3 py-1.5 text-[13px] font-medium text-white transition-all duration-200 hover:enabled:from-purple-600 hover:enabled:to-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:bg-none"
                >
                  <FileText size={14} />
                  商品情報をコピー
                </button>
              </div>
              <textarea
                placeholder="商品・ストーリー・登場人物・伝えたい内容などを自由に入力してください..."
                value={productInfo}
                onChange={(e) => setProductInfo(e.target.value)}
                className={MANGA_TEXTAREA}
                disabled={isLoading}
                rows={4}
              />
            </>
          )}
        </div>

        {/* Section 2: Style Selection (modal button - like banner page) */}
        <div data-tutorial="ads-manga-style">
          <label className={MANGA_FORM_LABEL} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            スタイルを選ぶ
            {(inputMode === 'product' ? selectedProductData : productInfo.trim()) && !selectedMangaStyleId && (
              <span className="ml-2 text-xs font-normal text-blue-500">（必須）</span>
            )}
            <div className="relative inline-flex group">
              <button
                type="button"
                className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="スタイルについて"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
              <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all duration-200 absolute left-full ml-2 top-0 w-72 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-4 leading-relaxed z-50 pointer-events-none">
                <div className="font-bold text-sm mb-2">スタイルとは？</div>
                <p className="mb-3">
                  スタイルとは、広告クリエイティブ制作におけるレイアウト構造・情報配置・表現ルールの方向性を示すフォーマットです。
                </p>
                <p className="mb-3">
                  ここで定義されるのは、どの要素をどの順序で見せるかという設計指針であり、コピー内容や商品、具体的なビジュアルは含みません。
                </p>
                <p className="text-gray-300">
                  生成結果が参考スタイルと同じデザイン・構成になるとは限りません。
                </p>
                <div className="absolute left-[-6px] top-2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-gray-900" />
              </div>
            </div>
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setShowMangaStyleModal(true)}
              disabled={isLoading}
              className={
                productInfo.trim() && !selectedMangaStyleId
                  ? 'flex cursor-pointer items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-4 py-2 text-sm text-blue-700 transition-all duration-200 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60'
                  : 'flex cursor-pointer items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 transition-all duration-200 hover:enabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60'
              }
            >
              <Settings size={16} />
              スタイルを選ぶ
            </button>
            {selectedMangaStyleData && (
              <div className="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-1.5">
                <span className="text-sm font-medium text-blue-700">{selectedMangaStyleData.name}</span>
                <button
                  type="button"
                  onClick={handleClearMangaStyle}
                  className="flex cursor-pointer items-center justify-center bg-transparent p-0.5 text-blue-500 hover:text-blue-700"
                >
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Section 3: Reference Images (buttons row - like banner page) */}
        <div>
          <label className={MANGA_FORM_LABEL}>参考画像（任意）</label>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setShowImageSelectionModal(true)}
              disabled={isLoading}
              className={MANGA_BTN_SECONDARY}
            >
              <FolderOpen size={16} />
              商品画像から選択
            </button>
            <button
              type="button"
              onClick={() => setShowBannerLibraryModal(true)}
              disabled={isLoading}
              className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-6 py-3 text-sm font-medium text-blue-700 transition-all duration-200 hover:enabled:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <ImageIcon size={16} />
              自社バナーから選択
            </button>
            <button
              type="button"
              onClick={() => uploadInputRef.current?.click()}
              disabled={isLoading}
              className={MANGA_BTN_SECONDARY}
            >
              <Upload size={16} />
              アップロード
            </button>
            <input
              ref={uploadInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            {(referenceImages.length > 0 || uploadedReferenceImages.length > 0) && (
              <span className="text-sm text-gray-600">
                {referenceImages.length + uploadedReferenceImages.length}枚選択中
              </span>
            )}
          </div>

          {/* Reference Images Preview */}
          {(referenceImages.length > 0 || uploadedReferenceImages.length > 0) && (
            <div className="flex flex-wrap gap-2" style={{ marginTop: '12px' }}>
              {referenceImages.map((img) => (
                <div key={`product-${img.id}`} className={MANGA_REF_IMAGE_ITEM}>
                  <img src={img.url} alt={img.fileName} className={MANGA_REF_IMAGE_IMG} />
                  <div className={MANGA_REF_IMAGE_OVERLAY}>
                    <button
                      type="button"
                      onClick={() => handleRemoveReferenceImage(img.id)}
                      className={MANGA_REF_REMOVE_BTN}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <span className={MANGA_REF_IMAGE_NAME}>{img.fileName}</span>
                </div>
              ))}
              {uploadedReferenceImages.map((img) => (
                <div key={img.id} className={MANGA_REF_IMAGE_ITEM_UPLOADED}>
                  <img src={img.url} alt={img.fileName} className={MANGA_REF_IMAGE_IMG} />
                  <div className={MANGA_REF_IMAGE_OVERLAY}>
                    <button
                      type="button"
                      onClick={() => handleRemoveUploadedRefImage(img.id)}
                      className={MANGA_REF_REMOVE_BTN}
                    >
                      <X size={14} />
                    </button>
                  </div>
                  <span className={MANGA_REF_IMAGE_NAME}>{img.fileName}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Section 4: Visual Style (画風) */}
        <div>
          <label className={MANGA_FORM_LABEL}>画風</label>
          <div className="grid grid-cols-4 gap-2.5 max-[768px]:grid-cols-2">
            {visualStyleOptions.map(style => (
              <button
                key={style.id}
                type="button"
                onClick={() => setMangaVisualStyle(style.id)}
                className={
                  mangaVisualStyle === style.id
                    ? 'flex cursor-pointer flex-col items-center gap-1.5 rounded-xl border-2 border-indigo-500 bg-[#eff0fe] px-2.5 py-3.5 text-center shadow-[0_0_0_1px_#6366f1] transition-all duration-200'
                    : 'flex cursor-pointer flex-col items-center gap-1.5 rounded-xl border-2 border-gray-200 bg-white px-2.5 py-3.5 text-center transition-all duration-200 hover:border-[#a9abf7]'
                }
                disabled={isLoading}
              >
                <span className="text-2xl">{style.emoji}</span>
                <span className="text-sm font-semibold text-gray-800">{style.name}</span>
                <span className="text-xs leading-[1.3] text-gray-500">{style.description}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Section 5: Settings grid (like banner page bottom row) */}
        <div className="grid grid-cols-[1fr_1fr_1fr_auto] items-end gap-3 max-[768px]:grid-cols-2">
          {/* Page Count */}
          <div>
            <label className={MANGA_SETTING_LABEL}>ページ数</label>
            <TaskySelect
              value={pageCount}
              onChange={(e) => setPageCount(Number(e.target.value))}
              disabled={isLoading}
            >
              {MANGA_PAGE_COUNT_OPTIONS.map(count => (
                <option key={count} value={count}>{count}ページ</option>
              ))}
            </TaskySelect>
          </div>

          {/* Aspect Ratio */}
          <div>
            <label className={MANGA_SETTING_LABEL}>画像サイズ</label>
            <TaskySelect
              value={aspectRatio}
              onChange={(e) => setAspectRatio(e.target.value as '9:16' | '1:1' | '16:9')}
              disabled={isLoading}
            >
              {aspectRatioOptions.map(option => (
                <option key={option.id} value={option.id}>{option.name} ({option.id})</option>
              ))}
            </TaskySelect>
          </div>

          {/* Color Mode */}
          <div>
            <label className={MANGA_SETTING_LABEL}>色調</label>
            <TaskySelect
              value={colorMode}
              onChange={(e) => setColorMode(e.target.value as 'color' | 'monochrome')}
              disabled={isLoading}
            >
              {colorModeOptions.map(option => (
                <option key={option.id} value={option.id}>{option.name}</option>
              ))}
            </TaskySelect>
          </div>

          {/* Generate Button */}
          <div className="flex items-end">
            <button
              data-tutorial="ads-manga-generate"
              onClick={handleCreateProject}
              disabled={
                isLoading ||
                (inputMode === 'product' ? !selectedProductData : !productInfo.trim()) ||
                (inputMode === 'product' && !selectedMangaStyleId && !additionalInstructions.trim())
              }
              className="flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-2 text-sm font-semibold text-white shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)] transition-all duration-200 hover:enabled:from-blue-700 hover:enabled:to-indigo-700 hover:enabled:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)] disabled:cursor-not-allowed disabled:from-gray-400 disabled:to-gray-500"
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} className="animate-spin" />
                  生成中...
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  漫画を生成
                </>
              )}
            </button>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className={MANGA_ERROR}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}
      </div>

      {/* Manga Style Select Modal */}
      <MangaStyleSelectModal
        isOpen={showMangaStyleModal}
        onClose={() => setShowMangaStyleModal(false)}
        onSelect={handleMangaStyleSelect}
        currentStyleId={selectedMangaStyleId}
      />
    </div>
  );

  // Render editing UI - New 2-column layout
  const renderEditingUI = () => {
    if (!mangaProject) return null;

    const selectedPage = mangaProject.pages.find(p => p.page_number === selectedPageNumber);

    return (
      <div ref={editSectionRef} className="flex min-h-[600px] flex-col gap-5">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 pb-4 max-[600px]:flex-col max-[600px]:items-stretch max-[600px]:gap-3">
          <div className="flex items-center gap-3 [&>svg]:text-indigo-500">
            <Wand2 size={24} />
            <h2 className="text-2xl font-semibold text-gray-800">ストーリー編集</h2>
          </div>
          <div className="flex gap-3 max-[600px]:justify-end">
            <button
              onClick={handleStartGeneration}
              disabled={isLoading || generatingPages.size > 0}
              className={MANGA_BTN_SECONDARY}
            >
              <Play size={16} />
              全ページを生成
            </button>
            <button onClick={handleReset} className={MANGA_BTN_GHOST}>
              キャンセル
            </button>
          </div>
        </div>

        {error && (
          <div className={MANGA_ERROR}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* 2-column area */}
        <div className="flex flex-1 gap-6 max-[900px]:flex-col">
          {/* Left Column - Preview Panel */}
          <div className="flex min-w-[400px] max-w-[600px] flex-[0_0_50%] flex-col gap-4 max-[900px]:min-w-full max-[900px]:max-w-full max-[900px]:flex-none">
            {/* 選択中画像を上部に大きく表示 */}
            <SelectedPagePreview
              page={selectedPage}
              aspectClass={mangaAspectClass(mangaProject.aspect_ratio ?? aspectRatio)}
              isGenerating={generatingPages.has(selectedPageNumber)}
              onOpenModal={() => {
                if (selectedPage) {
                  openImageModal(mangaProject.pages, selectedPageNumber - 1);
                }
              }}
            />

            {/* サムネイルを下部に横並び表示 */}
            <PageThumbnailGrid
              pages={mangaProject.pages}
              selectedPageNumber={selectedPageNumber}
              generatingPages={generatingPages}
              onSelectPage={handleSelectPage}
            />

            <CharacterListCompact
              characters={mangaProject.characters || []}
              onEditCharacter={(id) => setEditingCharacterId(id)}
              onGenerateAllImages={handleGenerateAllCharacterImages}
              onRegenerateImage={handleRegenerateCharacterImage}
              isGeneratingImages={isGeneratingCharacterImages}
              generatingCharacterId={generatingCharacterId}
            />
          </div>

          {/* Right Column - Edit Panel */}
          <div className="min-w-[400px] flex-1 max-[900px]:min-w-full">
            {selectedPage && (
              <PageEditPanel
                page={selectedPage}
                characters={mangaProject.characters || []}
                isGenerating={generatingPages.has(selectedPageNumber)}
                onGenerate={(data) => handleGeneratePageInLayout(selectedPageNumber, data)}
                onCancel={handleReset}
                referenceImages={pageReferenceImages[selectedPageNumber] || []}
                onOpenImageSelection={() => setShowEditImageSelectionModal(true)}
                onRemoveReferenceImage={(imageId) => {
                  setPageReferenceImages(prev => ({
                    ...prev,
                    [selectedPageNumber]: (prev[selectedPageNumber] || []).filter(img => img.id !== imageId)
                  }));
                }}
                uploadedImages={pageUploadedImages[selectedPageNumber] || []}
                onUploadImages={(files) => handleImageUpload(selectedPageNumber, files)}
                onRemoveUploadedImage={(imageId) => handleRemoveUploadedImage(selectedPageNumber, imageId)}
                isUploadingImage={isUploadingImage}
                productInfo={productInfo}
                onEditProductInfo={() => setShowEditProductInfoModal(true)}
              />
            )}
          </div>
        </div>

        {/* 編集画面用の商材選択モーダル */}
        <ProductImageSelectionModal
          isOpen={showEditImageSelectionModal}
          onClose={() => setShowEditImageSelectionModal(false)}
          onSelect={(selectedImages) => {
            setPageReferenceImages(prev => ({
              ...prev,
              [selectedPageNumber]: [...(prev[selectedPageNumber] || []), ...selectedImages]
            }));
            setShowEditImageSelectionModal(false);
          }}
          maxSelection={5}
        />

        {/* 編集画面用の商品情報モーダル */}
        <ProductInfoCopyModal
          isOpen={showEditProductInfoModal}
          onClose={() => setShowEditProductInfoModal(false)}
          onCopyToPrompt={(info) => {
            setProductInfo(info);
            setShowEditProductInfoModal(false);
          }}
        />

        {/* Character Editor Modal */}
        {editingCharacterId && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50" onClick={() => setEditingCharacterId(null)}>
            <div className={`max-h-[90vh] w-[90%] max-w-[500px] overflow-y-auto rounded-xl bg-white ${MANGA_CARD_SHADOW_HOVER}`} onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
                <h3 className="text-[1.1rem] font-semibold">キャラクター編集</h3>
                <button onClick={() => setEditingCharacterId(null)} className={MANGA_BTN_ICON}>
                  <X size={20} />
                </button>
              </div>
              {mangaProject.characters?.find(c => c.id === editingCharacterId) && (
                <CharacterEditor
                  character={mangaProject.characters.find(c => c.id === editingCharacterId)!}
                  onSave={handleUpdateCharacter}
                  onCancel={() => setEditingCharacterId(null)}
                />
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render generation progress
  const renderGeneratingUI = () => {
    if (!mangaProject || !generationJob) return null;

    const progress = generationJob.progress_percentage || 0;
    const completedCount = generationJob.completed_pages || 0;
    const totalCount = generationJob.total_pages || mangaProject.pages.length;
    const currentPage = generationJob.current_page || 0;

    // Determine current stage
    const getStageStatus = (stageNum: number) => {
      if (progress >= stageNum * 33) return 'completed';
      if (progress >= (stageNum - 1) * 33) return 'active';
      return 'pending';
    };

    const stages = [
      { num: 1, label: 'プロンプト生成', icon: <FileText size={18} /> },
      { num: 2, label: '画像生成', icon: <ImageIcon size={18} /> },
      { num: 3, label: '仕上げ処理', icon: <Sparkles size={18} /> },
    ];

    return (
      <div className={`rounded-2xl bg-[linear-gradient(135deg,#f8fafc_0%,#eef2ff_100%)] p-10 max-[768px]:p-5 ${MANGA_CARD_SHADOW}`}>
        {/* Header with animated icon */}
        <div className="mb-8 flex items-center gap-6">
          <div className="relative h-20 w-20 shrink-0">
            <div className="absolute inset-0 rounded-full border-[3px] border-transparent border-t-indigo-500 animate-[ring-spin_2s_linear_infinite]" />
            <div className="absolute inset-2 rounded-full border-[3px] border-transparent border-r-indigo-500/50 animate-[ring-spin_2.5s_linear_infinite_reverse]" />
            <div className="absolute inset-4 rounded-full border-[3px] border-transparent border-b-indigo-500/30 animate-[ring-spin_3s_linear_infinite]" />
            <div className="absolute inset-6 flex items-center justify-center rounded-full bg-white shadow-[0_4px_12px_rgba(99,102,241,0.2)]">
              <Wand2 size={32} className="text-indigo-500 animate-[wand-pulse_2s_ease-in-out_infinite]" />
            </div>
          </div>
          <div>
            <h2 className="mb-2 text-2xl font-bold text-gray-800">漫画を生成中</h2>
            <p className="text-sm text-gray-500">
              AIが素敵な漫画を作成しています。少々お待ちください。
            </p>
          </div>
        </div>

        {/* Progress stages */}
        <div className={`mb-8 flex items-center justify-center gap-0 rounded-xl bg-white p-6 ${MANGA_CARD_SHADOW}`}>
          {stages.map((stage, index) => (
            <React.Fragment key={stage.num}>
              <div className="flex flex-col items-center gap-2">
                <div className={MANGA_STAGE_ICON[getStageStatus(stage.num)]}>
                  {getStageStatus(stage.num) === 'completed' ? (
                    <Check size={18} />
                  ) : getStageStatus(stage.num) === 'active' ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    stage.icon
                  )}
                </div>
                <span className={MANGA_STAGE_LABEL[getStageStatus(stage.num)]}>{stage.label}</span>
              </div>
              {index < stages.length - 1 && (
                <div
                  className={
                    getStageStatus(stage.num + 1) !== 'pending'
                      ? 'mx-3 mb-7 h-[3px] w-[60px] rounded-sm bg-gradient-to-r from-emerald-500 to-indigo-500 transition-all duration-200'
                      : 'mx-3 mb-7 h-[3px] w-[60px] rounded-sm bg-gray-200 transition-all duration-200'
                  }
                />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Main progress section */}
        <div className={`mb-6 rounded-xl bg-white p-6 ${MANGA_CARD_SHADOW}`}>
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">生成進行状況</span>
            <span className="text-2xl font-bold text-indigo-500">{progress}%</span>
          </div>
          <div className="mb-4 h-3 overflow-hidden rounded-md bg-gray-100">
            <div
              className="relative h-full rounded-md bg-gradient-to-r from-indigo-500 to-indigo-400 transition-[width] duration-500 ease-out"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.3),transparent)] animate-[glow-move_2s_linear_infinite]" />
            </div>
          </div>
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-1.5 text-[13px] text-gray-600 [&>svg]:text-emerald-500">
              <Check size={14} />
              {completedCount} / {totalCount} ページ完了
            </span>
            {currentPage > 0 && (
              <span className="flex items-center gap-1.5 text-[13px] text-gray-600 [&>svg]:text-indigo-500">
                <Loader2 size={14} className="animate-spin" />
                ページ {currentPage} を生成中
              </span>
            )}
          </div>
        </div>

        {/* Preview grid */}
        <div className={`mb-6 rounded-xl bg-white p-6 ${MANGA_CARD_SHADOW}`}>
          <div className="mb-4">
            <h4 className="mb-4 flex items-center gap-2 text-center text-sm font-medium text-gray-600 [&>svg]:text-indigo-500">
              <BookOpen size={18} />
              ページプレビュー
            </h4>
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(140px,1fr))] gap-4">
            {mangaProject.pages.map((page) => {
              const status = page.generation_status || 'pending';
              return (
                <div
                  key={page.page_number}
                  className={MANGA_PREVIEW_ITEM[status] ?? MANGA_PREVIEW_ITEM.pending}
                >
                  <div className={`flex ${mangaAspectClass(mangaProject.aspect_ratio ?? aspectRatio)} items-center justify-center bg-gray-50`}>
                    {status === 'completed' && page.generated_image_url ? (
                      <img
                        src={page.generated_image_url}
                        alt={`ページ ${page.page_number}`}
                        className="h-full w-full object-contain"
                      />
                    ) : status === 'generating' ? (
                      <div className="flex flex-col items-center justify-center gap-2 bg-indigo-500/5 text-indigo-500">
                        <Loader2 size={24} className="animate-spin" />
                        <span className="text-[11px] font-medium">生成中</span>
                      </div>
                    ) : status === 'failed' ? (
                      <div className="flex flex-col items-center justify-center gap-2 bg-red-500/5 text-red-500">
                        <AlertCircle size={24} />
                        <span className="text-[11px] font-medium">失敗</span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-2 text-gray-400">
                        <ImageIcon size={24} />
                        <span className="text-[11px] font-medium">待機中</span>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-3 py-2">
                    <span className="text-[13px] font-semibold text-gray-700">{page.page_number}</span>
                    <span className={MANGA_PREVIEW_BADGE[status] ?? MANGA_PREVIEW_BADGE.pending}>
                      {status === 'completed' && <Check size={12} />}
                      {status === 'generating' && <Loader2 size={12} className="animate-spin" />}
                      {status === 'failed' && <AlertCircle size={12} />}
                      {status === 'pending' && <span>-</span>}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tips section */}
        <div className="rounded-lg border border-dashed border-indigo-500/30 bg-indigo-500/5 px-5 py-4">
          <div className="flex items-center gap-2.5 text-[13px] text-gray-600 [&>svg]:shrink-0 [&>svg]:text-indigo-500">
            <Sparkles size={16} />
            <span>生成中も他の作業を行えます。</span>
          </div>
        </div>
      </div>
    );
  };

  // Render completed manga
  const renderCompletedUI = () => {
    if (!mangaProject) return null;

    const completedPages = mangaProject.pages.filter(p => p.generated_image_url);
    const failedPages = mangaProject.pages.filter(p => p.generation_status === 'failed');

    return (
      <div ref={completeSectionRef} className={`rounded-xl bg-white p-8 max-[768px]:p-5 ${MANGA_CARD_SHADOW}`}>
        {/* Success header */}
        <div className="mb-6 flex items-center gap-5 rounded-xl bg-[linear-gradient(135deg,rgba(16,185,129,0.1)_0%,rgba(16,185,129,0.05)_100%)] p-6">
          <div className="relative flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
            <div className="absolute inset-[-6px] rounded-full border-[3px] border-emerald-500/30 animate-[success-ring-pulse_2s_ease-out_infinite]" />
            <Check size={28} />
          </div>
          <div>
            <h2 className="mb-1 text-[22px] font-bold text-gray-800">漫画が完成しました！</h2>
            <p className="text-sm text-gray-500">素敵な広告漫画ができあがりました</p>
          </div>
        </div>

        {/* Stats cards */}
        <div className="mb-6 grid grid-cols-[repeat(auto-fit,minmax(140px,1fr))] gap-4">
          <div className={MANGA_STAT_CARD}>
            <div className={`${MANGA_STAT_ICON} text-indigo-500`}>
              <BookOpen size={20} />
            </div>
            <div className="flex flex-col">
              <span className={`${MANGA_STAT_VALUE} text-gray-800`}>{mangaProject.total_pages}</span>
              <span className={MANGA_STAT_LABEL}>ページ数</span>
            </div>
          </div>
          <div className={MANGA_STAT_CARD}>
            <div className={`${MANGA_STAT_ICON} text-emerald-500`}>
              <Check size={20} />
            </div>
            <div className="flex flex-col">
              <span className={`${MANGA_STAT_VALUE} text-emerald-500`}>{completedPages.length}</span>
              <span className={MANGA_STAT_LABEL}>生成完了</span>
            </div>
          </div>
          <div className={MANGA_STAT_CARD}>
            <div className={`${MANGA_STAT_ICON} text-indigo-500`}>
              <Users size={20} />
            </div>
            <div className="flex flex-col">
              <span className={`${MANGA_STAT_VALUE} text-gray-800`}>{mangaProject.characters?.length ?? 0}</span>
              <span className={MANGA_STAT_LABEL}>キャラクター</span>
            </div>
          </div>
          {failedPages.length > 0 && (
            <div className={MANGA_STAT_CARD_ERROR}>
              <div className={`${MANGA_STAT_ICON} text-red-500`}>
                <AlertCircle size={20} />
              </div>
              <div className="flex flex-col">
                <span className={`${MANGA_STAT_VALUE} text-red-500`}>{failedPages.length}</span>
                <span className={MANGA_STAT_LABEL}>失敗</span>
              </div>
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4 rounded-lg bg-gray-50 px-5 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownloadZip}
              disabled={isDownloading}
              className={MANGA_BTN_DOWNLOAD}
            >
              {isDownloading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  ダウンロード中...
                </>
              ) : (
                <>
                  <Download size={18} />
                  全ページをダウンロード
                </>
              )}
            </button>
            <button
              onClick={openSmartEditSelector}
              className={MANGA_BTN_SECONDARY}
            >
              <Wand2 size={18} />
              スマート編集
            </button>
            <button
              onClick={() => setShowStoryPreview(true)}
              className={MANGA_BTN_SECONDARY}
            >
              <Eye size={18} />
              ストーリープレビュー
            </button>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setGenerationStep('editing')}
              className={MANGA_BTN_SECONDARY}
            >
              <Edit3 size={18} />
              編集に戻る
            </button>
            <button onClick={handleReset} className={MANGA_BTN_GHOST}>
              <Plus size={18} />
              新規作成
            </button>
          </div>
        </div>

        {/* 公開設定（Tasky 上の公開URL・最終コマの CTA） */}
        <MangaPublicationPanel project={mangaProject} onUpdated={applyPublicationResult} />

        {/* Gallery grid */}
        <div className="rounded-xl bg-gray-50 p-6">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-base font-semibold text-gray-800 [&>svg]:text-indigo-500">
              <ImageIcon size={18} />
              完成した漫画ページ
            </h3>
            <span className="rounded-xl bg-indigo-500 px-3 py-1 text-xs font-semibold text-white">
              {completedPages.length}枚
            </span>
          </div>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-5">
            {mangaProject.pages.map((page, index) => {
              const status = page.generation_status || (page.generated_image_url ? 'completed' : 'pending');
              return (
                <div
                  key={page.page_number}
                  className={MANGA_GALLERY_ITEM[status] ?? MANGA_GALLERY_ITEM.pending}
                >
                  <div
                    className={`relative ${mangaAspectClass(mangaProject.aspect_ratio ?? aspectRatio)} cursor-pointer bg-gray-100`}
                    onClick={() => page.generated_image_url && openImageModal(mangaProject.pages, index)}
                  >
                    {page.generated_image_url ? (
                      <img src={page.generated_image_url} alt={`ページ ${page.page_number}`} className="h-full w-full object-contain" />
                    ) : (
                      <div className="flex h-full w-full flex-col items-center justify-center gap-2 text-gray-400 [&>span]:text-xs [&>span]:font-medium">
                        {status === 'failed' ? (
                          <>
                            <AlertCircle size={28} />
                            <span>生成失敗</span>
                          </>
                        ) : (
                          <>
                            <ImageIcon size={28} />
                            <span>未生成</span>
                          </>
                        )}
                      </div>
                    )}

                    {/* Hover overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-all duration-200 group-hover:opacity-100">
                      <div className="flex gap-2">
                        {page.generated_image_url && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openImageModal(mangaProject.pages, index);
                            }}
                            className={MANGA_GALLERY_ACTION_BTN}
                            title="拡大表示"
                          >
                            <ZoomIn size={18} />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPageNumber(page.page_number);
                            setEditingPageNumber(page.page_number);
                            setGenerationStep('editing');
                          }}
                          className={MANGA_GALLERY_ACTION_BTN}
                          title="編集"
                        >
                          <Edit3 size={18} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRegeneratePage(page.page_number);
                          }}
                          className={MANGA_GALLERY_ACTION_BTN}
                          title="再生成"
                        >
                          <RefreshCw size={18} />
                        </button>
                      </div>
                    </div>

                    {/* Status badge */}
                    <div className={MANGA_GALLERY_BADGE[status] ?? MANGA_GALLERY_BADGE.pending}>
                      {status === 'completed' && <Check size={12} />}
                      {status === 'failed' && <AlertCircle size={12} />}
                      {status === 'pending' && <span>-</span>}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 bg-white p-3">
                    <span className="block text-sm font-semibold text-gray-800">
                      ページ {page.page_number}
                    </span>
                    {page.scene_description && (
                      <span className="mt-0.5 block overflow-hidden text-ellipsis whitespace-nowrap text-[11px] text-gray-500" title={page.scene_description}>
                        {page.scene_description.substring(0, 20)}...
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {error && (
          <div className={MANGA_ERROR}>
            <AlertCircle size={16} />
            {error}
          </div>
        )}
      </div>
    );
  };

  // Render history list
  const renderHistoryList = () => {
    if (projectHistory.length === 0 && !isLoadingHistory) {
      return null;
    }

    const getStatusBadgeText = (status: string) => {
      switch (status) {
        case 'draft': return '下書き';
        case 'in_progress': return '編集中';
        case 'generating': return '生成中';
        case 'generating_characters': return 'キャラ生成中';
        case 'completed': return '完了';
        default: return status;
      }
    };

    return (
      <div data-tutorial="ads-manga-results" className="mt-10 border-t border-gray-200 pt-10">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[linear-gradient(135deg,rgba(99,102,241,0.1)_0%,rgba(99,102,241,0.05)_100%)] text-indigo-500">
              <FolderOpen size={20} />
            </div>
            <div>
              <h3 className="flex items-center gap-2.5 text-lg font-semibold text-gray-800">生成した漫画一覧</h3>
            </div>
          </div>
          <span className="rounded-2xl bg-gray-100 px-3.5 py-1.5 text-[13px] font-semibold text-gray-600">
            全{historyTotalCount}件
          </span>
        </div>

        {/* Empty state */}
        {projectHistory.length === 0 && !isLoadingHistory && (
          <div className="flex flex-col items-center justify-center px-6 py-12 text-center text-gray-400">
            <ImageIcon size={48} className="mb-4 opacity-50" />
            <p className="text-sm">漫画はまだありません</p>
          </div>
        )}

        {/* Grid */}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-5 max-[768px]:!grid-cols-1">
          {projectHistory.map((project) => {
            // Null safety for pages and characters (some legacy projects may have null values)
            const pages = project.pages || [];
            const characters = project.characters || [];
            const thumbnailPage = pages.find(p => p.generated_image_url);
            const style = mangaStyles.find(s => s.visual_style === project.style);
            const completedPagesCount = pages.filter(p => p.generated_image_url).length;

            // Click handler for card
            const handleCardClick = async () => {
              try {
                const response = await mangaGenerationService.getProject(project.id);
                if (response.success) {
                  setMangaProject(response.data);
                  setGenerationStep(project.status === 'completed' ? 'complete' : 'editing');
                }
              } catch (err) {
                console.error('Error loading project:', err);
              }
            };

            return (
              <div key={project.id} className="overflow-hidden rounded-xl border border-gray-200 bg-white transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-1px_rgba(0,0,0,0.06)]">
                {/* Clickable area - Thumbnail and Content */}
                <div
                  className="group cursor-pointer focus:outline focus:outline-2 focus:outline-offset-[-2px] focus:outline-indigo-500"
                  onClick={handleCardClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
                >
                {/* Thumbnail section */}
                <div className="relative aspect-[16/9] overflow-hidden bg-[linear-gradient(135deg,#f3f4f6_0%,#f9fafb_100%)]">
                  {thumbnailPage?.generated_image_url ? (
                    <img
                      src={thumbnailPage.generated_image_url}
                      alt={`${project.product_info.substring(0, 20)}...`}
                      className="h-full w-full object-cover transition-transform duration-300 ease-[ease] group-hover:scale-[1.02]"
                    />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center text-gray-300">
                      <ImageIcon size={36} />
                    </div>
                  )}
                  <div className="absolute left-2.5 top-2.5 flex gap-2">
                    <span className={MANGA_HISTORY_BADGE_STATUS[project.status] ?? MANGA_HISTORY_BADGE_STATUS.default}>
                      {project.status === 'generating' || project.status === 'generating_characters' ? (
                        <Loader size={10} className="animate-spin" />
                      ) : project.status === 'completed' ? (
                        <Check size={10} />
                      ) : (
                        <FileEdit size={10} />
                      )}
                      {getStatusBadgeText(project.status)}
                    </span>
                    <span className={`${MANGA_HISTORY_BADGE} bg-indigo-500/90`}>
                      <BookOpen size={12} />
                      {project.total_pages}P
                    </span>
                    {style && (
                      <span className={`${MANGA_HISTORY_BADGE_STYLE}`}>
                        {style.name}
                      </span>
                    )}
                    {project.is_public && (
                      <span className={`${MANGA_HISTORY_BADGE} bg-emerald-500/90`}>
                        <Share2 size={12} />
                        公開中
                      </span>
                    )}
                  </div>
                </div>

                {/* Content section */}
                <div className="p-4">
                  <p className="mb-2 line-clamp-2 text-sm font-semibold leading-[1.4] text-gray-800" title={project.product_info}>
                    {project.product_info.length > 40
                      ? project.product_info.substring(0, 40) + '...'
                      : project.product_info}
                  </p>

                  <div className="mb-3 flex items-center gap-1.5 text-xs text-gray-500">
                    <span className="font-medium text-indigo-500">
                      {style?.name || project.style}
                    </span>
                    <span className="text-gray-300">•</span>
                    <span>
                      {new Date(project.created_at).toLocaleDateString('ja-JP', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </span>
                  </div>

                  {/* Page thumbnails */}
                  <div className="mb-3 flex gap-1.5">
                    {pages.slice(0, 4).map((page) => (
                      <div key={page.page_number} className="h-[54px] w-10 overflow-hidden rounded bg-gray-100">
                        {page.generated_image_url ? (
                          <img
                            src={page.generated_image_url}
                            alt={`ページ ${page.page_number}`}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <span className="text-[10px] font-medium text-gray-400">{page.page_number}</span>
                          </div>
                        )}
                      </div>
                    ))}
                    {pages.length > 4 && (
                      <div className="flex h-[54px] w-10 items-center justify-center rounded bg-gray-100 text-[11px] font-semibold text-gray-500">
                        +{pages.length - 4}
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-4">
                    <span className="flex items-center gap-1 text-xs text-gray-500 [&>svg]:text-emerald-500">
                      <Check size={12} />
                      {completedPagesCount}/{project.total_pages} 完了
                    </span>
                    <span className="flex items-center gap-1 text-xs text-gray-500 [&>svg]:text-emerald-500">
                      <Users size={12} />
                      {characters.length} キャラ
                    </span>
                  </div>
                </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3">
                  {project.status === 'completed' ? (
                    <>
                      <button
                        onClick={async () => {
                          try {
                            const response = await mangaGenerationService.getProject(project.id);
                            if (response.success) {
                              setMangaProject(response.data);
                              setGenerationStep('complete');
                            }
                          } catch (err) {
                            console.error('Error loading project:', err);
                          }
                        }}
                        className={MANGA_HISTORY_ACTION_BTN_PRIMARY}
                      >
                        <ZoomIn size={14} />
                        詳細を見る
                      </button>
                      <button
                        onClick={async () => {
                          try {
                            const response = await mangaGenerationService.getProject(project.id);
                            if (response.success) {
                              setMangaProject(response.data);
                              setGenerationStep('complete');
                              if (getSmartEditablePages(response.data).length > 0) {
                                setShowSmartPageSelector(true);
                              } else {
                                setError('編集できる生成済みページがありません');
                              }
                            }
                          } catch (err) {
                            console.error('Error loading project for smart edit:', err);
                          }
                        }}
                        className={MANGA_HISTORY_ACTION_BTN}
                      >
                        <Wand2 size={14} />
                        スマート編集
                      </button>
                      <button
                        onClick={() => mangaGenerationService.downloadMangaZip(project.id)}
                        className={MANGA_HISTORY_ACTION_BTN}
                      >
                        <Download size={14} />
                        ZIP
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={async () => {
                          try {
                            const response = await mangaGenerationService.getProject(project.id);
                            if (response.success) {
                              setMangaProject(response.data);
                              setGenerationStep('editing');
                            }
                          } catch (err) {
                            console.error('Error loading project:', err);
                          }
                        }}
                        className={MANGA_HISTORY_ACTION_BTN_PRIMARY}
                      >
                        <Edit3 size={14} />
                        編集を続ける
                      </button>
                      <button
                        onClick={async () => {
                          if (await confirm({ title: 'この下書きを削除しますか？', tone: 'destructive', confirmText: '削除する' })) {
                            try {
                              await mangaGenerationService.deleteProject(project.id);
                              refreshHistory();
                            } catch (err) {
                              console.error('Error deleting project:', err);
                            }
                          }
                        }}
                        className={MANGA_HISTORY_ACTION_BTN_DANGER}
                      >
                        <Trash2 size={14} />
                        削除
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Loading state */}
        {isLoadingHistory && (
          <div className="flex flex-col items-center justify-center gap-3 p-8 text-gray-500">
            <Loader2 size={24} className="animate-spin text-indigo-500" />
            <span className="text-sm">読み込み中...</span>
          </div>
        )}

        {/* Load more button */}
        {hasMoreHistory && !isLoadingHistory && (
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setHistoryPage(prev => prev + 1)}
              className="inline-flex min-w-[200px] cursor-pointer items-center gap-2 rounded-3xl border-2 border-gray-200 bg-white px-8 py-3 text-sm font-medium text-gray-600 transition-all duration-200 hover:border-indigo-500 hover:bg-gray-50 hover:text-indigo-500"
            >
              <Plus size={16} />
              もっと見る
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full">
      <BetaAlert featureName="広告漫画生成" />
      {generationStep === 'idle' && renderSetupForm()}
      {generationStep === 'creating' && (
        <Loading overlay={true} size="lg" text="ストーリー構成を生成中..." />
      )}
      {generationStep === 'editing' && renderEditingUI()}
      {generationStep === 'generating' && renderGeneratingUI()}
      {generationStep === 'complete' && renderCompletedUI()}

      {/* Show history list in idle and complete states */}
      {(generationStep === 'idle' || generationStep === 'complete') && renderHistoryList()}

      <ProductImageSelectionModal
        isOpen={showImageSelectionModal}
        onClose={() => setShowImageSelectionModal(false)}
        onSelect={handleReferenceImagesSelect}
        maxSelection={5}
      />

      <BannerLibraryModal
        isOpen={showBannerLibraryModal}
        onClose={() => setShowBannerLibraryModal(false)}
        onSelect={handleBannerLibrarySelect}
        maxSelection={5}
      />

      {showProductSelectionModal && (
        <ProductSelectionModal
          isOpen={showProductSelectionModal}
          onClose={() => setShowProductSelectionModal(false)}
          onConfirm={handleProductSelected}
        />
      )}

      <ProductInfoCopyModal
        isOpen={showFreeTextProductInfoModal}
        onClose={() => setShowFreeTextProductInfoModal(false)}
        onCopyToPrompt={(text) => {
          handleCopyToPrompt(text);
          setShowFreeTextProductInfoModal(false);
        }}
      />

      {/* Smart edit page selector */}
      {showSmartPageSelector && mangaProject && (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-slate-900/[0.55] p-6 max-[640px]:p-3" onClick={() => setShowSmartPageSelector(false)}>
          <div className="flex max-h-[min(760px,92vh)] w-[min(920px,100%)] flex-col overflow-hidden rounded-lg bg-white shadow-[0_24px_80px_rgba(15,23,42,0.25)]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-5 max-[640px]:px-4 max-[640px]:py-4">
              <div>
                <h3 className="mb-1.5 text-lg font-bold text-gray-900">編集したいページを選択</h3>
              </div>
              <button
                type="button"
                className="grid h-9 w-9 cursor-pointer place-items-center rounded-lg border-0 bg-gray-100 text-gray-700 hover:bg-gray-200"
                onClick={() => setShowSmartPageSelector(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3.5 overflow-auto px-6 pb-6 pt-5 max-[640px]:grid-cols-[repeat(2,minmax(0,1fr))] max-[640px]:px-4 max-[640px]:pb-4 max-[640px]:pt-4">
              {getSmartEditablePages().map((page) => (
                <button
                  key={page.page_number}
                  type="button"
                  className="cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-white p-0 text-left transition-[border-color,box-shadow,transform] duration-[160ms] ease-[ease] hover:-translate-y-px hover:border-blue-600 hover:shadow-[0_10px_28px_rgba(37,99,235,0.14)]"
                  onClick={() => handleSmartPageSelect(page)}
                >
                  <div className="aspect-square bg-gray-100">
                    <img src={page.generated_image_url || ''} alt={`ページ ${page.page_number}`} className="block h-full w-full object-cover" />
                  </div>
                  <div className="px-3 pb-3 pt-2.5">
                    <span className="block text-[13px] font-bold text-gray-900">ページ {page.page_number}</span>
                    {page.scene_description && <small className="mt-1 line-clamp-2 text-[11px] leading-[1.35] text-gray-500">{page.scene_description}</small>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Shared smart image editor */}
      {smartEditImage && smartEditingPage && (
        <SmartImageEditModal
          image={smartEditImage}
          isOpen={Boolean(smartEditImage)}
          onClose={() => setSmartEditingPage(null)}
          onApply={handleSmartEditApply}
        />
      )}

      {/* Image Modal */}
      {modalImageUrl && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/90 p-6" onClick={closeImageModal}>
          <div className="relative flex max-h-[90vh] max-w-[90vw] items-center justify-center" onClick={(e) => e.stopPropagation()}>
            <button className="absolute right-0 top-[-50px] z-10 cursor-pointer bg-transparent p-2 text-white transition-all duration-200 hover:scale-110 hover:text-gray-300" onClick={closeImageModal}>
              <X size={24} />
            </button>

            {modalPages.length > 1 && (
              <button
                className={`${MANGA_MODAL_NAV} left-[-60px]`}
                onClick={() => navigateModal('prev')}
              >
                <ChevronLeft size={32} />
              </button>
            )}

            <div className="flex flex-col items-center">
              <img
                src={modalImageUrl}
                alt={`ページ ${modalPages[modalPageIndex]?.page_number || ''}`}
                className="max-h-[min(80vh,calc(90vh-150px))] max-w-[80vw] rounded-lg object-contain shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
              />
              <div className="mt-4 flex items-center gap-4 text-sm text-white">
                <span className="rounded-[20px] bg-white/10 px-3 py-1.5">ページ {modalPages[modalPageIndex]?.page_number}</span>
                {modalPages.length > 1 && (
                  <span className="rounded-[20px] bg-white/10 px-3 py-1.5 text-gray-400">
                    {modalPageIndex + 1} / {modalPages.length}
                  </span>
                )}
                {(modalPages[modalPageIndex]?.image_versions?.length ?? 0) > 0 && (
                  <span className="rounded-[20px] bg-white/10 px-3 py-1.5 text-gray-300">
                    旧版 {modalPages[modalPageIndex]?.image_versions?.length}件
                  </span>
                )}
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-2.5">
                <button
                  type="button"
                  className={`${MANGA_MODAL_ACTION_BTN} bg-white/[0.16] hover:bg-white/[0.24]`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEditModalPage();
                  }}
                >
                  <Edit3 size={16} />
                  ページ全体を編集
                </button>
                {modalPages[modalPageIndex]?.generated_image_url && (
                  <button
                    type="button"
                    className={`${MANGA_MODAL_ACTION_BTN} bg-indigo-600/[0.92] hover:bg-indigo-700/[0.98]`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSmartEditModalPage();
                    }}
                  >
                    <Wand2 size={16} />
                    スマート編集
                  </button>
                )}
              </div>
              {(modalPages[modalPageIndex]?.image_versions?.length ?? 0) > 0 && (
                <div className="mt-2.5 flex flex-wrap items-center justify-center gap-2 text-white">
                  <span className="rounded-2xl bg-white/10 px-2.5 py-[5px] text-xs font-semibold text-gray-300">旧版</span>
                  {modalPages[modalPageIndex]?.image_versions?.map((version) => (
                    <button
                      type="button"
                      key={`${version.version}-${version.generated_s3_key || version.generated_image_url}`}
                      className="inline-flex min-h-[28px] cursor-pointer items-center gap-[5px] rounded-2xl border border-white/[0.18] bg-white/[0.14] px-2.5 py-[5px] text-xs font-semibold text-white transition-all duration-200 hover:border-white/[0.34] hover:bg-white/[0.24]"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRestoreModalVersion(version);
                      }}
                    >
                      <RefreshCw size={12} />
                      v{version.version}に戻す
                    </button>
                  ))}
                </div>
              )}
            </div>

            {modalPages.length > 1 && (
              <button
                className={`${MANGA_MODAL_NAV} right-[-60px]`}
                onClick={() => navigateModal('next')}
              >
                <ChevronRight size={32} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* ストーリープレビュー（公開ページと同じ縦スクロール表示） */}
      {showStoryPreview && mangaProject && (
        <div
          className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-black/80 p-6 max-[640px]:p-3"
          onClick={() => setShowStoryPreview(false)}
        >
          <div
            className="relative my-8 w-full max-w-[540px] rounded-xl bg-white p-4 shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between gap-3">
              <h3 className="flex items-center gap-2 text-base font-semibold text-gray-800 [&>svg]:text-indigo-500">
                <BookOpen size={18} />
                ストーリープレビュー
              </h3>
              <button
                type="button"
                aria-label="閉じる"
                onClick={() => setShowStoryPreview(false)}
                className="cursor-pointer rounded-lg p-1.5 text-gray-500 transition-colors duration-200 hover:bg-gray-100 hover:text-gray-800"
              >
                <X size={20} />
              </button>
            </div>
            <div className="max-h-[75vh] overflow-y-auto">
              <MangaStoryScroll
                pages={buildStoryPreviewPages(mangaProject)}
                aspectRatio={mangaProject.aspect_ratio ?? aspectRatio}
                title={mangaProject.product_data?.name ?? mangaProject.product_info}
                cta={
                  mangaProject.cta_url
                    ? {
                        text: mangaProject.cta_text?.trim() || MANGA_DEFAULT_CTA_TEXT,
                        url: mangaProject.cta_url,
                      }
                    : null
                }
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Character Editor Component
interface CharacterEditorProps {
  character: MangaCharacter;
  onSave: (character: MangaCharacter) => void;
  onCancel: () => void;
}

const CharacterEditor: React.FC<CharacterEditorProps> = ({ character, onSave, onCancel }) => {
  const [name, setName] = useState(character.name);
  const [appearance, setAppearance] = useState(character.appearance);
  const [role, setRole] = useState(character.role);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ ...character, name, appearance, role });
  };

  const roleOptions: { value: MangaCharacter['role']; label: string; icon: string }[] = [
    { value: 'protagonist', label: '主人公', icon: '⭐' },
    { value: 'supporting', label: '脇役', icon: '👤' },
    { value: 'extra', label: 'その他', icon: '👥' },
  ];

  return (
    <form onSubmit={handleSubmit} className="rounded-xl border-2 border-indigo-500 bg-white p-5 shadow-[0_4px_12px_rgba(99,102,241,0.15)]">
      <div className="mb-5 flex items-center gap-3 border-b border-indigo-500/20 pb-4">
        <div className={MANGA_CHAR_AVATAR_EDIT[role] ?? MANGA_CHAR_AVATAR_EDIT.extra}>
          {name.charAt(0) || '?'}
        </div>
        <span className="rounded-[20px] bg-indigo-500/10 px-2.5 py-1 text-xs font-semibold text-indigo-500">編集中</span>
      </div>

      <div className="flex flex-col gap-4">
        <div>
          <label className={MANGA_EDITOR_FIELD_LABEL}>
            <User size={14} />
            名前
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={MANGA_EDITOR_FIELD_INPUT}
            placeholder="キャラクター名"
          />
        </div>

        <div>
          <label className={MANGA_EDITOR_FIELD_LABEL}>
            <Star size={14} />
            役割
          </label>
          <div className="flex gap-2">
            {roleOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                className={mangaRoleOptionClass(option.value, role === option.value)}
                onClick={() => setRole(option.value)}
              >
                <span className="text-base">{option.icon}</span>
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className={MANGA_EDITOR_FIELD_LABEL}>
            <Eye size={14} />
            外見・特徴
          </label>
          <textarea
            value={appearance}
            onChange={(e) => setAppearance(e.target.value)}
            className={MANGA_EDITOR_FIELD_TEXTAREA}
            placeholder="髪の色、服装、特徴的な外見など"
            rows={3}
          />
        </div>
      </div>

      <div className="mt-5 flex justify-end gap-2.5 border-t border-indigo-500/20 pt-4">
        <button type="button" onClick={onCancel} className={MANGA_BTN_SECONDARY_SM}>
          <X size={14} />
          キャンセル
        </button>
        <button type="submit" className={MANGA_BTN_PRIMARY_SM}>
          <Check size={14} />
          保存
        </button>
      </div>
    </form>
  );
};

// Page Editor Component
interface PageEditorProps {
  page: MangaPage;
  characters: MangaCharacter[];
  onSave: (data: Partial<MangaPage>) => void;
  onCancel: () => void;
}

const PageEditor: React.FC<PageEditorProps> = ({ page, characters, onSave, onCancel }) => {
  const [sceneDescription, setSceneDescription] = useState(page.scene_description || page.description || '');
  const [narration, setNarration] = useState(page.narration || '');
  const [dialogues, setDialogues] = useState<MangaDialogue[]>(page.dialogues || []);
  const [additionalInstructions, setAdditionalInstructions] = useState(page.additional_instructions || '');

  const handleAddDialogue = () => {
    const newDialogue: MangaDialogue = {
      id: `dlg-${Date.now()}`,
      character_id: characters[0]?.id || '',
      text: '',
      order: dialogues.length + 1,
    };
    setDialogues([...dialogues, newDialogue]);
  };

  const handleUpdateDialogue = (id: string, updates: Partial<MangaDialogue>) => {
    setDialogues(dialogues.map(d => d.id === id ? { ...d, ...updates } : d));
  };

  const handleRemoveDialogue = (id: string) => {
    setDialogues(dialogues.filter(d => d.id !== id));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      scene_description: sceneDescription,
      narration,
      dialogues,
      additional_instructions: additionalInstructions,
    });
  };

  // Get character name by ID
  const getCharacterName = (characterId: string): string => {
    const character = characters.find(c => c.id === characterId);
    return character?.name || '不明';
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="flex items-center justify-between border-b border-gray-200 pb-4 max-[600px]:flex-col max-[600px]:items-stretch max-[600px]:gap-3">
        <div>{page.page_number}</div>
        <span className="rounded-[20px] bg-indigo-500/10 px-2.5 py-1 text-xs font-semibold text-indigo-500">編集中</span>
      </div>

      <div>
        {/* Scene Section */}
        <div>
          <div>
            <ImageIcon size={14} />
            シーン説明
          </div>
          <textarea
            value={sceneDescription}
            onChange={(e) => setSceneDescription(e.target.value)}
            className={MANGA_TEXTAREA}
            placeholder="このページで描かれるシーンを説明してください..."
            rows={3}
          />
        </div>

        {/* Narration Section */}
        <div>
          <div>
            <FileText size={14} />
            ナレーション
          </div>
          <textarea
            value={narration}
            onChange={(e) => setNarration(e.target.value)}
            className={MANGA_TEXTAREA}
            placeholder="コマの外に表示される地の文を入力..."
            rows={2}
          />
        </div>

        {/* Dialogues Section */}
        <div>
          <div>
            <MessageSquare size={14} />
            セリフ
            <button
              type="button"
              onClick={handleAddDialogue}
              className={MANGA_BTN_ADD_SM}
            >
              <Plus size={14} />
              追加
            </button>
          </div>
          <div>
            {dialogues.length === 0 ? (
              <div>
                <MessageSquare size={20} />
                <span>セリフがありません</span>
                <button
                  type="button"
                  onClick={handleAddDialogue}
                  className={MANGA_BTN_ADD_SM}
                >
                  <Plus size={14} />
                  セリフを追加
                </button>
              </div>
            ) : (
              dialogues.map((dialogue, index) => (
                <div key={dialogue.id}>
                  <div>{index + 1}</div>
                  <div>
                    <div>
                      <TaskySelect
                        value={dialogue.character_id}
                        onChange={(e) => handleUpdateDialogue(dialogue.id, { character_id: e.target.value })}
                        className="text-sm"
                      >
                        {characters.map(c => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </TaskySelect>
                    </div>
                    <div>
                      <input
                        type="text"
                        value={dialogue.text}
                        onChange={(e) => handleUpdateDialogue(dialogue.id, { text: e.target.value })}
                        className={MANGA_INPUT}
                        placeholder={`「${getCharacterName(dialogue.character_id)}のセリフ...」`}
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveDialogue(dialogue.id)}
                    className={MANGA_BTN_ICON}
                    title="削除"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Additional Instructions Section */}
        <div>
          <div>
            <Sparkles size={14} />
            追加指示
            <span>（オプション）</span>
          </div>
          <textarea
            value={additionalInstructions}
            onChange={(e) => setAdditionalInstructions(e.target.value)}
            className={MANGA_TEXTAREA}
            placeholder="画像生成時の追加指示（構図、雰囲気、強調したい要素など）..."
            rows={2}
          />
        </div>
      </div>

      <div className="flex gap-3 max-[600px]:justify-end">
        <button type="button" onClick={onCancel} className={MANGA_BTN_SECONDARY}>
          <X size={16} />
          キャンセル
        </button>
        <button type="submit" className={MANGA_BTN_PRIMARY}>
          <Check size={16} />
          保存
        </button>
      </div>
    </form>
  );
};

// ============================================
// New 2-Column Layout Components
// ============================================

// Page Thumbnail Grid Component
interface PageThumbnailGridProps {
  pages: MangaPage[];
  selectedPageNumber: number;
  generatingPages: Set<number>;
  onSelectPage: (pageNumber: number) => void;
}

const PageThumbnailGrid: React.FC<PageThumbnailGridProps> = ({
  pages, selectedPageNumber, generatingPages, onSelectPage
}) => {
  return (
    <div className="manga-thumbnail-strip flex gap-2 overflow-x-auto py-2">
      {pages.map(page => {
        const isSelected = page.page_number === selectedPageNumber;
        const isGenerating = generatingPages.has(page.page_number);
        const status = page.generation_status || 'pending';
        const isFailed = status === 'failed';

        return (
          <div
            key={page.page_number}
            className={mangaThumbnailMiniClass(isSelected, isGenerating)}
            onClick={() => !isGenerating && onSelectPage(page.page_number)}
            title={isFailed && page.error_message ? `エラー: ${page.error_message}` : undefined}
          >
            {page.generated_image_url ? (
              <img src={page.generated_image_url} alt={`ページ ${page.page_number}`} className="h-full w-full object-cover" />
            ) : isFailed ? (
              <div className="flex h-full w-full flex-col items-center justify-center bg-red-500/10 text-red-500 [&>svg]:mb-1">
                <AlertCircle size={16} />
              </div>
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-gray-100 text-xs font-semibold text-gray-400">
                {isGenerating ? <Loader2 className="animate-spin" size={16} /> : page.page_number}
              </div>
            )}
            <div className="absolute bottom-0.5 right-0.5 rounded-sm bg-gray-800/80 px-1 py-px text-[0.65rem] font-semibold text-white">
              {page.page_number}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Selected Page Preview Component
interface SelectedPagePreviewProps {
  page: MangaPage | undefined;
  isGenerating: boolean;
  onOpenModal: () => void;
  /** mangaAspectClass() が返す表示枠クラス（1:1 を 3:4 に切らないため） */
  aspectClass: string;
}

const SelectedPagePreview: React.FC<SelectedPagePreviewProps> = ({
  page, isGenerating, onOpenModal, aspectClass
}) => {
  const isFailed = page?.generation_status === 'failed';

  return (
    <div className={`group relative order-1 w-full ${aspectClass} cursor-pointer overflow-hidden rounded-xl bg-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.06)]`} onClick={page?.generated_image_url ? onOpenModal : undefined}>
      {/* ページ番号バッジ */}
      {page && (
        <div className="absolute left-2 top-2 rounded bg-gray-800/85 px-2.5 py-1 text-xs font-semibold text-white">
          ページ {page.page_number}
        </div>
      )}
      {isGenerating ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-gray-500">
          <Loader2 size={32} className="animate-spin text-indigo-500" />
          <span>生成中...</span>
        </div>
      ) : isFailed ? (
        <div className="flex h-full min-h-[200px] flex-col items-center justify-center bg-[linear-gradient(135deg,#fcdada,#fbd0d0)] p-6 text-center text-red-500">
          <AlertCircle size={48} className="mb-3 opacity-80" />
          <span className="mb-2 text-base font-semibold">生成に失敗しました</span>
          {page?.error_message && (
            <p className="mt-2 max-w-full break-words rounded-lg bg-white/50 px-3 py-2 text-[0.85rem] text-[#bf3636]">{page.error_message}</p>
          )}
        </div>
      ) : page?.generated_image_url ? (
        <>
          <img src={page.generated_image_url} alt={`ページ ${page.page_number}`} className="h-full w-full bg-white object-contain" />
          <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-[20px] bg-gray-800/80 px-3 py-1.5 text-xs text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
            <ZoomIn size={16} />
            クリックで拡大
          </div>
        </>
      ) : (
        <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-gray-400">
          <ImageIcon size={48} className="opacity-50" />
          <span>未生成</span>
        </div>
      )}
    </div>
  );
};

// Character List Compact Component
interface CharacterListCompactProps {
  characters: MangaCharacter[];
  onEditCharacter: (characterId: string) => void;
  onGenerateAllImages?: () => void;
  onRegenerateImage?: (characterId: string) => void;
  isGeneratingImages?: boolean;
  generatingCharacterId?: string | null;
}

const CharacterListCompact: React.FC<CharacterListCompactProps> = ({
  characters,
  onEditCharacter,
  onGenerateAllImages,
  onRegenerateImage,
  isGeneratingImages = false,
  generatingCharacterId = null,
}) => {
  const getRoleEmoji = (role: string) => {
    switch (role) {
      case 'protagonist': return '⭐';
      case 'supporting': return '👤';
      default: return '👥';
    }
  };

  const getStatusBadge = (status?: string) => {
    switch (status) {
      case 'completed':
        return <span className={`${MANGA_CHAR_STATUS_BADGE} bg-emerald-500 text-white`}>✓</span>;
      case 'generating':
        return <span className={`${MANGA_CHAR_STATUS_BADGE} bg-indigo-500 text-white`}><Loader size={10} className="animate-spin" /></span>;
      case 'failed':
        return <span className={`${MANGA_CHAR_STATUS_BADGE} bg-red-500 text-white`}>!</span>;
      default:
        return null;
    }
  };

  const hasAnyImages = characters.some(c => c.character_image_url);
  const allImagesGenerated = characters.length > 0 && characters.every(c => c.character_image_status === 'completed');
  const hasFailedImages = characters.some(c => c.character_image_status === 'failed');

  return (
    <div className={`rounded-lg bg-white p-3 ${MANGA_CARD_SHADOW}`}>
      <div className="mb-2 flex items-center gap-2 border-b border-gray-200 pb-2 text-sm font-medium text-gray-600 [&>svg]:text-gray-400">
        <Users size={16} />
        <span>キャラクター ({characters.length})</span>
        {onGenerateAllImages && !allImagesGenerated && (
          <button
            className="ml-auto flex cursor-pointer items-center gap-1 rounded-xl bg-indigo-500 px-2.5 py-1 text-[0.7rem] font-medium text-white transition-all duration-200 hover:enabled:bg-indigo-600 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={(e) => {
              e.stopPropagation();
              onGenerateAllImages();
            }}
            disabled={isGeneratingImages}
            title="全キャラクターの画像を生成"
          >
            {isGeneratingImages ? (
              <Loader size={12} className="animate-spin" />
            ) : (
              <ImageIcon size={12} />
            )}
            <span>{hasAnyImages ? '再生成' : '画像生成'}</span>
          </button>
        )}
        {allImagesGenerated && (
          <span className="ml-auto flex items-center gap-1 rounded-xl bg-emerald-500/10 px-2.5 py-1 text-[0.7rem] font-medium text-emerald-500">
            <CheckCircle size={12} />
            生成済み
          </span>
        )}
      </div>
      <div className="flex max-h-[300px] flex-col gap-2 overflow-y-auto pr-1">
        {characters.map(character => {
          const isThisGenerating = generatingCharacterId === character.id ||
            character.character_image_status === 'generating';

          const getRoleName = (role: string) => {
            switch (role) {
              case 'protagonist': return '主人公';
              case 'supporting': return '脇役';
              default: return 'その他';
            }
          };

          return (
            <div
              key={character.id}
              className={mangaCharacterCardClass(character.role)}
              onClick={() => onEditCharacter(character.id)}
            >
              <div className={mangaCharacterCardImageClass(character.role)}>
                {character.character_image_url ? (
                  <img
                    src={character.character_image_url}
                    alt={character.name}
                    className="h-full w-full object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                      (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                    }}
                  />
                ) : null}
                <div className={`flex h-full w-full items-center justify-center ${character.character_image_url ? 'hidden' : ''}`}>
                  <span className="text-2xl font-semibold text-gray-500">{character.name.charAt(0)}</span>
                </div>
                {getStatusBadge(character.character_image_status)}
                {onRegenerateImage && character.character_image_url && (
                  <button
                    className={MANGA_CHAR_REGEN_BTN}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRegenerateImage(character.id);
                    }}
                    disabled={isThisGenerating || isGeneratingImages}
                    title="画像を再生成"
                  >
                    {isThisGenerating ? (
                      <Loader size={14} className="animate-spin" />
                    ) : (
                      <RefreshCw size={14} />
                    )}
                  </button>
                )}
              </div>
              <div className="flex min-w-0 flex-1 flex-col justify-center gap-0.5 px-3 py-2">
                <div className="flex items-center gap-1 text-sm font-semibold text-gray-800">
                  <span className="text-[0.8rem]">{getRoleEmoji(character.role)}</span>
                  {character.name}
                </div>
                <div className="text-xs text-gray-500">{getRoleName(character.role)}</div>
                {character.appearance && (
                  <div className="overflow-hidden text-ellipsis whitespace-nowrap text-xs leading-[1.3] text-gray-600">
                    {character.appearance.length > 50
                      ? character.appearance.substring(0, 50) + '...'
                      : character.appearance}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {hasFailedImages && (
        <div className="mt-2 rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-500">
          一部のキャラクター画像の生成に失敗しました。再生成をお試しください。
        </div>
      )}
    </div>
  );
};

// Page Edit Panel Component (for 2-column layout)
interface PageEditPanelProps {
  page: MangaPage;
  characters: MangaCharacter[];
  isGenerating: boolean;
  onGenerate: (data: Partial<MangaPage>) => void;
  onCancel: () => void;
  // 商材素材関連
  referenceImages?: SelectedProductImage[];
  onOpenImageSelection?: () => void;
  onRemoveReferenceImage?: (imageId: number) => void;
  // 画像アップロード関連
  uploadedImages?: UploadedImage[];
  onUploadImages?: (files: File[]) => void;
  onRemoveUploadedImage?: (imageId: string) => void;
  isUploadingImage?: boolean;
  // 商品情報関連
  productInfo?: string;
  onEditProductInfo?: () => void;
}

const PageEditPanel: React.FC<PageEditPanelProps> = ({
  page, characters, isGenerating, onGenerate, onCancel,
  referenceImages = [], onOpenImageSelection, onRemoveReferenceImage,
  uploadedImages = [], onUploadImages, onRemoveUploadedImage, isUploadingImage = false,
  productInfo = '', onEditProductInfo
}) => {
  const [sceneDescription, setSceneDescription] = useState(page.scene_description || page.description || '');
  const [narration, setNarration] = useState(page.narration || '');
  const [dialogues, setDialogues] = useState<MangaDialogue[]>(page.dialogues || []);
  const [additionalInstructions, setAdditionalInstructions] = useState(page.additional_instructions || '');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset form when page changes
  useEffect(() => {
    setSceneDescription(page.scene_description || page.description || '');
    setNarration(page.narration || '');
    setDialogues(page.dialogues || []);
    setAdditionalInstructions(page.additional_instructions || '');
  }, [page]);

  const handleAddDialogue = () => {
    const newDialogue: MangaDialogue = {
      id: `dlg-${Date.now()}`,
      character_id: characters[0]?.id || '',
      text: '',
      order: dialogues.length + 1,
    };
    setDialogues([...dialogues, newDialogue]);
  };

  const handleUpdateDialogue = (id: string, updates: Partial<MangaDialogue>) => {
    setDialogues(dialogues.map(d => d.id === id ? { ...d, ...updates } : d));
  };

  const handleRemoveDialogue = (id: string) => {
    setDialogues(dialogues.filter(d => d.id !== id));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onGenerate({
      scene_description: sceneDescription,
      narration,
      dialogues,
      additional_instructions: additionalInstructions,
    });
  };

  const getCharacterName = (characterId: string): string => {
    const character = characters.find(c => c.id === characterId);
    return character?.name || '不明';
  };

  return (
    <form onSubmit={handleSubmit} className={`flex h-full flex-col rounded-xl bg-white p-5 ${MANGA_CARD_SHADOW}`}>
      <div className="mb-4 flex items-center justify-between border-b border-gray-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-500 text-base font-bold text-white">{page.page_number}</div>
          <h3 className="text-[1.1rem] font-semibold text-gray-800">ページ {page.page_number} の編集</h3>
        </div>
        {page.generation_status === 'completed' && (
          <span className="flex items-center gap-1 rounded-xl bg-emerald-500/10 px-2.5 py-1 text-xs font-medium text-emerald-500">
            <Check size={12} />
            生成済み
          </span>
        )}
        {page.generation_status === 'failed' && (
          <span className="flex items-center gap-1 rounded-xl bg-red-500/10 px-2.5 py-1 text-xs font-medium text-red-500">
            <AlertCircle size={12} />
            失敗
          </span>
        )}
      </div>

      {/* Error banner for failed pages */}
      {page.generation_status === 'failed' && (
        <div className="mb-4 flex gap-3 rounded-lg border border-[#f7a2a2] bg-[linear-gradient(135deg,#fbd0d0,#fcdada)] px-4 py-3">
          <div className="flex shrink-0 items-start pt-0.5 text-red-500">
            <AlertCircle size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-1 text-[0.9rem] font-semibold text-[#bf3636]">生成に失敗しました</div>
            {page.error_message && (
              <div className="mb-1.5 break-words rounded bg-white/50 px-2.5 py-1.5 text-[0.85rem] text-[#d73d3d]">{page.error_message}</div>
            )}
            <div className="text-[0.8rem] text-gray-600">
              内容を修正して「再試行」ボタンをクリックしてください
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto">
        {/* Correction instruction section */}
        <div className="flex flex-col gap-2 rounded-xl border border-indigo-500/[0.28] bg-indigo-500/[0.04] p-3.5">
          <div className="flex items-center justify-between gap-3">
            <label className={MANGA_EDIT_LABEL}>
              <Sparkles size={14} />
              修正指示
              <span className={MANGA_LABEL_HINT_SM}>（AI編集）</span>
            </label>
            {onUploadImages && (
              <>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingImage}
                  className={MANGA_BTN_ADD_SM_WHITE}
                >
                  {isUploadingImage ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Upload size={14} />
                  )}
                  画像を添付
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => {
                    const files = e.target.files;
                    if (files) {
                      onUploadImages(Array.from(files));
                    }
                    e.currentTarget.value = '';
                  }}
                />
              </>
            )}
          </div>
          <textarea
            value={additionalInstructions}
            onChange={(e) => setAdditionalInstructions(e.target.value)}
            className={MANGA_TEXTAREA}
            placeholder="例: 商品をもう少し大きく見せる / 背景を明るくする / 1コマ目の表情を笑顔にする"
            rows={4}
          />
          <div className="flex flex-col gap-2.5">
            {referenceImages.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <span className="text-[0.72rem] font-semibold text-gray-500">商材素材</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {referenceImages.map((img) => (
                    <div key={img.id} className="group relative h-12 w-12 overflow-hidden rounded border border-gray-200 bg-neutral-100">
                      <img src={img.url} alt={img.fileName} className="h-full w-full object-contain" />
                      {onRemoveReferenceImage && (
                        <button
                          type="button"
                          className={MANGA_THUMB_REMOVE_SM}
                          onClick={() => onRemoveReferenceImage(img.id)}
                          title="削除"
                        >
                          <X size={10} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {uploadedImages.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <span className="text-[0.72rem] font-semibold text-gray-500">添付画像</span>
                <div className="flex flex-wrap gap-2">
                  {uploadedImages.map((img) => (
                    <div key={img.id} className="group relative w-16 overflow-hidden rounded border border-gray-200 bg-gray-100 transition-transform duration-200 hover:scale-105">
                      <img src={img.url} alt={img.fileName} className="block h-16 w-16 object-cover" />
                      {onRemoveUploadedImage && (
                        <button
                          type="button"
                          className={MANGA_THUMB_REMOVE_UPLOADED}
                          onClick={() => onRemoveUploadedImage(img.id)}
                          title="削除"
                        >
                          <X size={10} />
                        </button>
                      )}
                      <span className="block overflow-hidden text-ellipsis whitespace-nowrap bg-gray-50 px-1 py-0.5 text-center text-[0.65rem] text-gray-500">{img.fileName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Scene Section */}
        <div className="flex flex-col gap-2">
          <label className={MANGA_EDIT_LABEL}>
            <ImageIcon size={14} />
            シーン説明
          </label>
          <textarea
            value={sceneDescription}
            onChange={(e) => setSceneDescription(e.target.value)}
            className={MANGA_TEXTAREA}
            placeholder="このページで描かれるシーンを説明してください..."
            rows={3}
          />
        </div>

        {/* Narration Section */}
        <div className="flex flex-col gap-2">
          <label className={MANGA_EDIT_LABEL}>
            <FileText size={14} />
            ナレーション
          </label>
          <textarea
            value={narration}
            onChange={(e) => setNarration(e.target.value)}
            className={MANGA_TEXTAREA}
            placeholder="コマの外に表示される地の文を入力..."
            rows={2}
          />
        </div>

        {/* Dialogues Section */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className={MANGA_EDIT_LABEL}>
              <MessageSquare size={14} />
              セリフ
            </label>
            <button
              type="button"
              onClick={handleAddDialogue}
              className={MANGA_BTN_ADD_SM}
            >
              <Plus size={14} />
              追加
            </button>
          </div>
          <div className="flex flex-col gap-2">
            {dialogues.length === 0 ? (
              <div className="rounded-lg bg-gray-50 p-4 text-center text-sm text-gray-400">
                <span>セリフがありません</span>
              </div>
            ) : (
              dialogues.map((dialogue, index) => (
                <div key={dialogue.id} className="flex items-center gap-2">
                  <div className="min-w-[100px]">
                    <TaskySelect
                      value={dialogue.character_id}
                      onChange={(e) => handleUpdateDialogue(dialogue.id, { character_id: e.target.value })}
                      className="h-8 text-sm"
                    >
                      {characters.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </TaskySelect>
                  </div>
                  <input
                    type="text"
                    value={dialogue.text}
                    onChange={(e) => handleUpdateDialogue(dialogue.id, { text: e.target.value })}
                    className={MANGA_INPUT_SM_PANEL}
                    placeholder={`「${getCharacterName(dialogue.character_id)}のセリフ...」`}
                  />
                  <button
                    type="button"
                    onClick={() => handleRemoveDialogue(dialogue.id)}
                    className="flex h-7 w-7 cursor-pointer items-center justify-center rounded bg-transparent p-0 text-gray-400 transition-all duration-200 hover:bg-red-500/10 hover:text-red-500"
                    title="削除"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* 商材素材セクション */}
        <div className="mt-2 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className={MANGA_EDIT_LABEL}>
              <FolderOpen size={14} />
              商材素材
              <span className={MANGA_LABEL_HINT_SM}>（オプション）</span>
            </label>
            {onOpenImageSelection && (
              <button
                type="button"
                onClick={onOpenImageSelection}
                className={MANGA_BTN_ADD_SM}
              >
                <Plus size={14} />
                素材を選択
              </button>
            )}
          </div>
          {referenceImages.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-2">
              {referenceImages.map((img) => (
                <div key={img.id} className="group relative h-12 w-12 overflow-hidden rounded border border-gray-200 bg-neutral-100">
                  <img src={img.url} alt={img.fileName} className="h-full w-full object-contain" />
                  {onRemoveReferenceImage && (
                    <button
                      type="button"
                      className={MANGA_THUMB_REMOVE_SM}
                      onClick={() => onRemoveReferenceImage(img.id)}
                      title="削除"
                    >
                      <X size={10} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-2 rounded bg-gray-50 p-3">
              <span className="text-[0.8rem] text-gray-500">商材画像を追加すると、より商品に合った漫画が生成されます</span>
            </div>
          )}
        </div>

        {/* 商品情報セクション */}
        <div className="mt-2 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className={MANGA_EDIT_LABEL}>
              <FileText size={14} />
              商品情報
              <span className={MANGA_LABEL_HINT_SM}>（参照用）</span>
            </label>
            {onEditProductInfo && (
              <button
                type="button"
                onClick={onEditProductInfo}
                className={MANGA_BTN_ADD_SM}
              >
                <Edit3 size={14} />
                編集
              </button>
            )}
          </div>
          {productInfo ? (
            <div className={MANGA_PRODUCT_INFO_PREVIEW}>
              {productInfo.substring(0, 200)}
              {productInfo.length > 200 && '...'}
            </div>
          ) : (
            <div className="mt-2 rounded-lg bg-gray-50 p-3 text-center">
              <span className="text-xs text-gray-400">商品情報が設定されていません</span>
            </div>
          )}
        </div>

      </div>

      <div className="mt-4 flex justify-end gap-3 border-t border-gray-200 pt-4">
        <button type="button" onClick={onCancel} className={MANGA_BTN_SECONDARY}>
          キャンセル
        </button>
        <button
          type="submit"
          disabled={isGenerating}
          className={page.generation_status === 'failed' ? MANGA_BTN_RETRY : MANGA_BTN_PRIMARY}
        >
          {isGenerating ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              生成中...
            </>
          ) : page.generation_status === 'failed' ? (
            <>
              <RefreshCw size={16} />
              再試行
            </>
          ) : page.generated_image_url ? (
            <>
              <RefreshCw size={16} />
              修正を反映
            </>
          ) : (
            <>
              <RefreshCw size={16} />
              生成
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default MangaGenerationTab;
